/*
 * Parisaa Luxe: motion layer and custom components.
 *
 * Every homepage section has its own choreography, chosen by
 * data-px-fx="name" on the section's content wrapper (see `fx` below).
 * Generic hooks still work anywhere else:
 *   data-px-reveal="up|left|right|fade|zoom|mask"  reveal one element
 *   data-px-stagger + [data-px-item] children       reveal children in turn
 *   data-px-split="lines|chars|words|blur"          heading reveal style
 *   data-px-parallax="12"                           drift an image by N percent
 *   data-px-count="250"                             count a number up
 *   data-px-magnetic                                button follows the pointer
 */
(() => {
  const config = window.PX || {};
  const html = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const hasGsap = typeof window.gsap !== 'undefined';
  const hasST = hasGsap && !!window.ScrollTrigger;
  const motion = hasST && html.classList.contains('px-motion');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  // Urdu (and other right-to-left scripts) join letters, so text is never split into characters.
  const rtl = html.dir === 'rtl';

  if (!motion) html.classList.remove('px-motion');

  if (hasGsap) {
    // Optional parts of a section (e.g. no products picked yet) are simply skipped.
    // Transforms stay 2D: text that slides or fades in is never lifted onto a
    // GPU layer mid-animation and dropped at the end (that hop changes its
    // anti-aliasing, which reads as a colour shift). Big photos get a fixed
    // layer in CSS instead (see "Stable layers" in px-theme.css).
    gsap.config({ nullTargetWarn: false, force3D: false });
    if (hasST) gsap.registerPlugin(ScrollTrigger);
    if (window.SplitText) gsap.registerPlugin(SplitText);
  }
  if (hasST) {
    ScrollTrigger.config({ ignoreMobileResize: true });
    ScrollTrigger.defaults({ fastScrollEnd: true });
  }

  /* ---------- Smooth scrolling ---------- */
  let lenis = null;
  if (config.smooth && window.Lenis && hasGsap && !reduced && !designMode) {
    lenis = new Lenis({
      lerp: config.lerp || 0.09,
      smoothWheel: true,
      prevent: (node) =>
        !!node.closest(
          '.drawer, .menu-drawer, .quick-add-modal, .predictive-search, .search-modal, .header__submenu, .mega-menu__content, .facets-container, [data-lenis-prevent]'
        ),
    });
    if (hasST) lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);

    // Dawn locks the page by adding overflow-hidden classes to <body>; pause Lenis while locked.
    new MutationObserver(() => {
      if (/overflow-hidden/.test(document.body.className)) lenis.stop();
      else lenis.start();
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="#"]');
      if (!link || link.getAttribute('href').length < 2) return;
      const target = document.querySelector(link.getAttribute('href'));
      if (!target) return;
      event.preventDefault();
      lenis.scrollTo(target, { offset: -(parseInt(getComputedStyle(html).getPropertyValue('--px-header-h')) || 0) });
    });
  }
  window.PXLenis = lenis;

  /* ---------- Scrollbar width (for the scroll lock in px-theme.css) ---------- */
  const measureScrollbar = () => {
    if (/overflow-hidden/.test(document.body.className)) return;
    html.style.setProperty('--px-sbw', `${Math.max(0, window.innerWidth - html.clientWidth)}px`);
  };
  measureScrollbar();
  window.addEventListener('resize', measureScrollbar, { passive: true });

  /* ---------- Warm up photos before they scroll in ----------
     Lazy photos used to start loading only as their section opened, so the
     opening stalled while they decoded. Now they load and decode about a
     screen ahead of the visitor. */
  if ('IntersectionObserver' in window) {
    const warm = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const img = entry.target;
      warm.unobserve(img);
      img.loading = 'eager';
      if (img.decode) img.decode().catch(() => {});
    }), { rootMargin: '0px 0px 120% 0px' });
    document.querySelectorAll('#MainContent img[loading="lazy"]').forEach((img) => warm.observe(img));
  }

  /* ---------- Recommendations load early ----------
     Dawn fetches "Complete the look" only when it is about to scroll into view,
     so it grew (and pushed the footer) in front of the visitor. Fetch it as soon
     as the page is idle instead, while it is still far below the screen. */
  window.addEventListener('load', () => {
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 600));
    idle(() => {
      document.querySelectorAll('product-recommendations').forEach((el) => {
        if (el.classList.contains('product-recommendations--loaded') || el.dataset.pxEarly || typeof el.loadRecommendations !== 'function') return;
        el.dataset.pxEarly = '1';
        if (el.observer) el.observer.disconnect();
        el.loadRecommendations(el.dataset.productId);
      });
    });
  }, { once: true });
  // They load early (off screen), so their entrance waits until they are seen.
  if ('IntersectionObserver' in window) {
    const seen = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('px-rec-seen');
      seen.unobserve(entry.target);
    }), { threshold: 0.2 });
    document.querySelectorAll('product-recommendations').forEach((el) => seen.observe(el));
  } else {
    document.querySelectorAll('product-recommendations').forEach((el) => el.classList.add('px-rec-seen'));
  }

  /* ---------- Hover effects pause while the page scrolls ----------
     Wide hover effects (the occasions panels grow on hover) would otherwise
     fire one after another as content slides under a still mouse. */
  let scrollRest;
  window.addEventListener('scroll', () => {
    if (!html.classList.contains('px-scrolling')) html.classList.add('px-scrolling');
    clearTimeout(scrollRest);
    scrollRest = setTimeout(() => html.classList.remove('px-scrolling'), 160);
  }, { passive: true });

  /* ---------- Back to top ---------- */
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-px-top]')) return;
    if (lenis) lenis.scrollTo(0, { duration: 2.2, easing: (t) => 1 - Math.pow(1 - t, 4) });
    else window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
  });

  /* ---------- Header state ---------- */
  const header = document.querySelector('.section-header');
  if (header) {
    const setHeight = () => html.style.setProperty('--px-header-h', `${header.offsetHeight}px`);
    setHeight();
    if (window.ResizeObserver) new ResizeObserver(setHeight).observe(header);
    const onScroll = () => document.body.classList.toggle('px-header-solid', window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }
  // The announcement line's height, so a homepage hero can reach up under it.
  const announcement = document.querySelector('.announcement-bar-section');
  if (announcement) {
    const setAnnouncement = () => html.style.setProperty('--px-ann-h', `${announcement.offsetHeight}px`);
    setAnnouncement();
    if (window.ResizeObserver) new ResizeObserver(setAnnouncement).observe(announcement);
  }

  /* ---------- Intro loader ----------
     The loader's own entrance runs in CSS, so it starts on the very first
     paint. Here we only wait for the page and lift the curtain. */
  // Set when the intro loader pulled its sheet off the whole hero (the hero
  // then skips its own opening, since the loader already revealed it).
  let heroUnveiled = false;
  // While the gold thread is still writing the name, the page's own setup
  // (sections, ScrollTrigger measuring) would hold the main thread and make
  // the writing stutter. So with the loader up, that setup waits until the
  // name is written and runs while the screen is still; the sheet only lifts
  // once it is done.
  const hasLoader = !!document.querySelector('.px-preloader');
  // The writing itself starts (class px-write) once this script has run, so
  // the theme's own start-up never lands on the first letters; the loader
  // snippet adds the class after 2s anyway if this script never arrives.
  let markWriting;
  const writing = new Promise((r) => (markWriting = r));
  const written = hasLoader ? writing.then((at) => new Promise((r) => setTimeout(r, Math.max(0, at + 3650 - performance.now())))) : Promise.resolve();
  let markBooted;
  const booted = new Promise((r) => (markBooted = r));
  const nextFrames = () => new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
  const introDone = new Promise((resolve) => {
    const loader = document.querySelector('.px-preloader');
    if (!loader) return resolve();
    // Long enough for the gold thread to write the whole name, counted from
    // when the loader first appeared (its CSS starts then, not at navigation).
    const shownUntilFrom = (at) => at + 3850;
    const pageReady = new Promise((r) => (document.readyState === 'complete' ? r() : window.addEventListener('load', r, { once: true })));
    // A slow network never holds the loader past 6s.
    const capped = new Promise((r) => setTimeout(r, Math.max(0, 6000 - performance.now())));
    Promise.race([pageReady, capped])
      .then(() => writing)
      .then((at) => new Promise((r) => setTimeout(r, Math.max(0, shownUntilFrom(at) - performance.now()))))
      .then(() => Promise.race([booted, new Promise((r) => setTimeout(r, 2500))]))
      .then(nextFrames)
      .then(() => {
        try {
          sessionStorage.setItem('px-intro', '1');
        } catch (e) {}
        const finish = () => loader.remove();
        if (!hasGsap || reduced) {
          finish();
          return resolve();
        }
        // The name fades, the gold thread slides down to the bottom edge and
        // widens to the full screen, then the sheet rises with the thread
        // along its lower edge. Transforms only, with an even ease (the old
        // expo.inOut raced through the middle and read as a jolt).
        const rule = loader.querySelector('.px-preloader__rule');
        heroUnveiled = true;
        gsap.set(loader, { animation: 'none' });
        const r = rule.getBoundingClientRect();
        gsap.set(rule, { animation: 'none', scaleX: 1 });
        gsap
          .timeline({ onComplete: finish })
          .to(loader.querySelector('.px-preloader__word'), { opacity: 0, duration: 0.6, ease: 'power1.inOut' }, 0)
          .to(rule, { y: window.innerHeight - 1 - r.top, scaleX: (window.innerWidth / Math.max(r.width, 1)) * 1.02, duration: 0.7, ease: 'power2.inOut' }, 0.05)
          .addLabel('pull', 0.75)
          .add(resolve, 'pull')
          .to(loader, { yPercent: -100, duration: 1.6, ease: 'power3.inOut' }, 'pull')
          .to(rule, { autoAlpha: 0, duration: 0.4 }, 'pull+=1.3');
      });
  });
  window.PXIntro = introDone;

  /* ---------- Page transitions ---------- */
  const curtain = document.querySelector('.px-curtain');
  if (curtain && hasGsap && !reduced && !designMode) {
    // Garnet veil (option C): it rises from the bottom with a gold thread on
    // its top edge, the small name fades in, and the next page is requested
    // as it closes. On arrival it lifts away with the thread on its lower
    // edge. The moves are Web Animations of transform, which the browser
    // runs off the main thread, so they stay smooth however busy the page
    // is while it loads. Failsafes make sure the veil can never stay.
    // (Once lifted it drops its arrival class: that class carries a CSS
    // failsafe that hides it after 3s, which would hide the next veil too.)
    const brand = curtain.querySelector('.px-curtain__brand');
    const EASE = 'cubic-bezier(0.65, 0, 0.35, 1)';
    let rescue = 0;
    let moves = [];
    const stop = () => {
      moves.forEach((a) => a.cancel());
      moves = [];
    };
    const settle = () => curtain.classList.remove('is-covering', 'is-rising', 'is-lifting');
    // Run a move and keep its end state as a plain style once it is done.
    const move = (el, frames, options) => {
      const a = el.animate(frames, { fill: 'forwards', ...options });
      moves.push(a);
      a.finished
        .then(() => {
          Object.assign(el.style, frames[frames.length - 1]);
          a.cancel();
        })
        .catch(() => {});
      return a;
    };
    let lifted = false;
    const lift = () => {
      if (lifted) return;
      lifted = true;
      stop();
      // The stylesheet may already be lifting it (after 1.4s): carry on from
      // wherever it is now, so it never jumps back down.
      const cs = getComputedStyle(curtain);
      const y = new DOMMatrixReadOnly(cs.transform === 'none' ? undefined : cs.transform).m42;
      const h = curtain.offsetHeight || window.innerHeight;
      const from = Math.max(-100, Math.min(0, (y / h) * 100));
      curtain.style.transform = `translateY(${from}%)`;
      settle();
      if (cs.visibility === 'hidden' || from <= -99.5) {
        curtain.style.transform = 'translateY(-100%)';
        return;
      }
      curtain.classList.add('is-lifting');
      move(brand, [{ opacity: getComputedStyle(brand).opacity }, { opacity: 0 }], { duration: 250 });
      move(curtain, [{ transform: `translateY(${from}%)` }, { transform: 'translateY(-100%)' }], { duration: 850 * (1 + from / 100), delay: from < 0 ? 0 : 80, easing: from < 0 ? 'cubic-bezier(0.33, 0, 0.35, 1)' : EASE })
        .finished.then(settle)
        .catch(() => {});
    };
    // Lift once this page's own setup has run, or after 0.8s at most: the
    // lift does not need the main thread, so it never waits long.
    const liftWhenReady = () => Promise.race([booted, new Promise((r) => setTimeout(r, 800))]).then(nextFrames).then(lift);
    if (curtain.classList.contains('is-covering')) liftWhenReady();
    document.addEventListener('px:veil', () => {
      lifted = false;
      liftWhenReady();
    });
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      clearTimeout(rescue);
      stop();
      settle();
      curtain.style.transform = 'translateY(-100%)';
    });
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.target === '_blank' || link.hasAttribute('download') || link.closest('[data-no-transition], .drawer, cart-drawer, .quick-add-modal, .menu-drawer, .predictive-search')) return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin || url.hash || /\/(cart|checkout|account\/logout)/.test(url.pathname)) return;
      if (url.pathname === location.pathname && url.search === location.search) return;
      event.preventDefault();
      try {
        sessionStorage.setItem('px-curtain', '1');
      } catch (e) {}
      stop();
      settle();
      lifted = false;
      curtain.classList.add('is-rising');
      move(curtain, [{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }], { duration: 600, easing: EASE });
      move(brand, [{ opacity: 0 }, { opacity: 1 }], { duration: 300, delay: 400 });
      // The next page is requested as the veil closes; the browser keeps this
      // page on screen until the new one is ready.
      setTimeout(() => (location.href = url.href), 550);
      // If the browser stays on this page (a cancelled or failed load), the
      // veil goes away again instead of hanging.
      clearTimeout(rescue);
      rescue = setTimeout(lift, 8000);
    });
  }

  /* ---------- Motion helpers ---------- */
  // Entrances start the moment an element peeks in, and play quicker the
  // faster the page is moving, so nothing is still animating once the
  // visitor is looking at it (or has already scrolled past it).
  const pace = (velocity) => 1.6 + Math.min(1.8, Math.max(0, (Math.abs(velocity || 0) - 350) / 600));
  const peek = (start) => start.replace(/^top (\d+)%$/, (m, n) => `top ${Math.max(+n, 97)}%`);
  const once = (trigger, start = 'top 85%') => ({
    trigger,
    start: peek(start),
    once: true,
    onEnter: (self) => self.animation && self.animation.timeScale(pace(self.getVelocity())),
  });
  // Same pacing for ScrollTrigger.batch() entrances.
  const batchPace = (tween, triggers) => tween.timeScale(pace(triggers && triggers[0] ? triggers[0].getVelocity() : 0));
  // A batch entrance built of several tweens: they go on one timeline so the
  // whole group is paced together.
  const batchRun = (triggers, build) => {
    const tl = gsap.timeline();
    build(tl);
    return batchPace(tl, triggers);
  };

  // Clip-path reveals need an explicit end shape (the computed value is
  // `none`, which can't be interpolated), and are cleared afterwards.
  const OPEN = 'inset(0% 0% 0% 0%)';
  // A cover in the section's colour, laid over a card; animating it away
  // (transform only) opens the card without any clip-path.
  function addCover(host) {
    const cover = document.createElement('span');
    cover.className = 'px-cover';
    cover.setAttribute('aria-hidden', 'true');
    host.appendChild(cover);
    return cover;
  }

  // Picks the ink for a badge drawn over a photo: the theme gold when the part
  // of the photo under it is dark, espresso when it is light. Reads a small
  // copy of the photo so the page's own image is never re-requested at size.
  function badgeInk(photo, badge) {
    const gold = getComputedStyle(html).getPropertyValue('--px-accent').trim() || '#b48a4e';
    const dark = '#2a1e1b';
    const img = photo.querySelector('img');
    if (!img || !img.currentSrc && !img.src) return Promise.resolve(gold);
    const box = badge.parentElement.getBoundingClientRect();
    const pr = photo.getBoundingClientRect();
    const cs = getComputedStyle(badge);
    const bw = parseFloat(cs.width);
    const bh = parseFloat(cs.height);
    const bx = box.left + parseFloat(cs.left);
    const by = box.bottom - parseFloat(cs.bottom) - bh;
    const x0 = Math.max(bx, pr.left), x1 = Math.min(bx + bw, pr.right);
    const y0 = Math.max(by, pr.top), y1 = Math.min(by + bh, pr.bottom);
    if (x1 <= x0 || y1 <= y0 || !pr.width) return Promise.resolve(gold);
    return new Promise((resolve) => {
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onload = () => {
        try {
          // Map the overlap through object-fit: cover onto the photo.
          const k = Math.max(pr.width / probe.naturalWidth, pr.height / probe.naturalHeight);
          const ox = (probe.naturalWidth * k - pr.width) / 2;
          const oy = (probe.naturalHeight * k - pr.height) / 2;
          const sx = (x0 - pr.left + ox) / k, sy = (y0 - pr.top + oy) / k;
          const sw = (x1 - x0) / k, sh = (y1 - y0) / k;
          const c = document.createElement('canvas');
          c.width = c.height = 16;
          const g = c.getContext('2d', { willReadFrequently: true });
          g.drawImage(probe, sx, sy, sw, sh, 0, 0, 16, 16);
          const d = g.getImageData(0, 0, 16, 16).data;
          let sum = 0, n = 0;
          for (let i = 0; i < d.length; i += 4) {
            if (d[i + 3] < 32) continue;
            sum += (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]) / 255;
            n += 1;
          }
          resolve(n && sum / n > 0.55 ? dark : gold);
        } catch (e) {
          resolve(gold);
        }
      };
      probe.onerror = () => resolve(gold);
      probe.src = (img.currentSrc || img.src).replace(/([?&])width=\d+/, '$1width=300');
    });
  }

  const unclip = (targets, from, { to = OPEN, ...vars } = {}) =>
    gsap.fromTo(targets, { clipPath: from }, { clipPath: to, clearProps: 'clipPath', ...vars });

  // Hover transitions on transform would fight a running tween, so they are
  // paused (class px-animating) until the entrance has finished.
  const calm = (targets) => {
    const els = gsap.utils.toArray(targets);
    els.forEach((el) => el.classList.add('px-animating'));
    return () => els.forEach((el) => el.classList.remove('px-animating'));
  };
  const split = (el, type) => (window.SplitText ? SplitText.create(el, { type, mask: type === 'chars' ? 'words' : type }) : null);
  const textUnit = (type) => (rtl && type === 'chars' ? 'words' : type);

  // Headline styles, picked per section so no two headings move alike.
  const headline = {
    lines(el) {
      SplitText.create(el, {
        type: 'lines',
        mask: 'lines',
        autoSplit: true,
        onSplit: (self) => gsap.from(self.lines, { yPercent: 110, duration: 1.2, ease: 'expo.out', stagger: 0.1, scrollTrigger: once(el, 'top 88%') }),
      });
    },
    chars(el) {
      const s = split(el, 'chars');
      gsap.set(el, { perspective: 600 });
      gsap.from(s.chars, {
        yPercent: 80, rotationX: -90, autoAlpha: 0, transformOrigin: '50% 100%',
        duration: 1, ease: 'power4.out', stagger: 0.025, scrollTrigger: once(el, 'top 88%'),
      });
    },
    words(el) {
      const s = split(el, 'words');
      gsap.from(s.words, { yPercent: 120, rotation: 6, duration: 1.2, ease: 'expo.out', stagger: 0.07, scrollTrigger: once(el, 'top 88%') });
    },
    blur(el) {
      const s = SplitText.create(el, { type: 'words' });
      gsap.from(s.words, {
        autoAlpha: 0, filter: 'blur(14px)', y: 24, scale: 1.04,
        duration: 1.4, ease: 'power3.out', stagger: 0.08, scrollTrigger: once(el, 'top 88%'),
        clearProps: 'filter',
      });
    },
  };

  function revealHeading(el) {
    gsap.set(el, { autoAlpha: 1 });
    if (!window.SplitText) return;
    (headline[textUnit(el.dataset.pxSplit)] || headline.lines)(el);
  }

  function revealEyebrow(el) {
    gsap.fromTo(
      el,
      { '--px-line': 0, letterSpacing: '0.7em', autoAlpha: 0 },
      { '--px-line': 1, letterSpacing: '0.35em', autoAlpha: 1, duration: 1.4, ease: 'expo.out', scrollTrigger: once(el, 'top 90%') }
    );
  }

  function revealSoft(targets, trigger, vars = {}) {
    if (!targets || (targets.length !== undefined && !targets.length)) return;
    gsap.from(targets, { y: 30, autoAlpha: 0, duration: 1, ease: 'power3.out', stagger: 0.08, scrollTrigger: once(trigger, 'top 85%'), ...vars });
  }

  // Shared intro for any section heading block (eyebrow, title, subtitle).
  function revealHead(root) {
    root.querySelectorAll('.px-head .px-eyebrow').forEach(revealEyebrow);
    root.querySelectorAll('[data-px-split]').forEach(revealHeading);
    root.querySelectorAll('.px-head .px-subtitle').forEach((el) => revealSoft(el, el, { delay: 0.1 }));
  }

  // One formatter for every count-up frame (toLocaleString builds a new one each call).
  const numberFormat = new Intl.NumberFormat();
  function countUp(root) {
    root.querySelectorAll('[data-px-count]').forEach((el) => {
      const target = parseFloat(el.dataset.pxCount) || 0;
      const counter = { value: 0 };
      gsap.to(counter, {
        value: target, duration: 2.2, ease: 'power2.out', scrollTrigger: once(el, 'top 92%'),
        onUpdate: () => {
          const text = numberFormat.format(Math.round(counter.value));
          if (el.firstChild && el.firstChild.nodeType === 3 && !el.firstChild.nextSibling) el.firstChild.nodeValue = text;
          else el.textContent = text;
        },
      });
    });
  }

  // Gold sparks burst out of an element (newsletter thanks, "Added to bag").
  function sparkBurst(el, count = 24, reach = 120) {
    if (!hasGsap || reduced || !el) return;
    const r = el.getBoundingClientRect();
    for (let i = 0; i < count; i++) {
      const spark = document.createElement('span');
      spark.className = 'px-spark';
      document.body.appendChild(spark);
      const angle = Math.random() * Math.PI * 2;
      const dist = reach * (0.5 + Math.random() * 0.8);
      gsap.fromTo(
        spark,
        { x: r.left + Math.random() * r.width, y: r.top + r.height / 2, scale: 0, rotation: 0 },
        {
          x: `+=${Math.cos(angle) * dist}`, y: `+=${Math.sin(angle) * dist - reach * 0.4}`, scale: 0.6 + Math.random() * 1.1,
          rotation: Math.random() * 360, opacity: 0, duration: 1.2 + Math.random() * 0.7, ease: 'expo.out',
          onComplete: () => spark.remove(),
        }
      );
    }
  }

  // A marquee driven by the scroll: it grows to full size as it rises into
  // view, then the section holds while scrolling down carries the text one
  // full round to the left, and the page moves on. Scrolling up runs it back.
  function scrollMarquee(marquee) {
    const inner = marquee.querySelector('.px-marquee__inner');
    const track = marquee.querySelector('.px-marquee__track');
    if (!inner || !track || reduced) return;
    // The line is read once: it starts with its first word on the page's left
    // edge and stops when its last word reaches the right edge.
    const section = marquee.closest('.shopify-section') || marquee;
    const edges = () => {
      const page = document.querySelector('#MainContent .page-width');
      const left = section.getBoundingClientRect().left;
      if (!page) return { l: 0, r: section.clientWidth };
      const box = page.getBoundingClientRect();
      const cs = getComputedStyle(page);
      return { l: box.left + parseFloat(cs.paddingLeft) - left, r: box.right - parseFloat(cs.paddingRight) - left };
    };
    const startX = () => edges().l;
    const endX = () => edges().r - track.offsetWidth;
    gsap.fromTo(marquee, { scale: 0.55, transformOrigin: '50% 50%' }, {
      scale: 1,
      ease: 'none',
      scrollTrigger: { trigger: section, start: 'top bottom', end: 'center center', scrub: 0.5 },
    });
    // A short line that already fits just sits centred.
    if (endX() >= startX()) {
      gsap.set(inner, { x: (section.clientWidth - track.offsetWidth) / 2 });
      return;
    }
    gsap.fromTo(inner, { x: startX }, {
      x: endX,
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'center center',
        end: () => `+=${Math.round((startX() - endX()) * 0.8)}`,
        pin: section,
        // #MainContent is a flex column, where ScrollTrigger would skip the spacing.
        pinSpacing: true,
        scrub: 0.5,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      },
    });
  }

  /* ---------- Section choreographies ---------- */
  const fx = {
    marquee(root) {
      const marquee = root.querySelector('.px-marquee');
      if (!marquee) return;
      if (marquee.classList.contains('px-marquee--scroll')) scrollMarquee(marquee);
      if (marquee.classList.contains('px-marquee--scroll')) {
        // Its arrival is the scroll-driven grow in scrollMarquee().
      } else if (marquee.classList.contains('px-marquee--outline')) {
        gsap.from(marquee, { autoAlpha: 0, y: 60, duration: 1.4, ease: 'expo.out', scrollTrigger: once(root, 'top 90%') });
      } else {
        unclip(marquee, 'inset(0% 50% 0% 50%)', { duration: 1.4, ease: 'power3.inOut', scrollTrigger: once(root, 'top 95%') });
      }

    },

    // Collection page: the arch opens upwards, the title types in, the
    // category pills follow; the photo drifts slowly as the page scrolls.
    colhero(root) {
      const arch = root.querySelector('.px-colhero__arch');
      const img = arch && arch.querySelector('img, svg');
      const tl = gsap.timeline({ paused: true });
      if (arch) tl.fromTo(arch, { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: OPEN, clearProps: 'clipPath', duration: 1.5, ease: 'expo.out' }, 0);
      if (img) tl.fromTo(img, { scale: 1.3 }, { scale: 1, duration: 2, ease: 'expo.out' }, 0);
      tl.from(root.querySelectorAll('.px-colhero__crumbs, .px-colhero__eyebrow, .px-colhero__desc'), { y: 24, autoAlpha: 0, duration: 1, ease: 'power3.out', stagger: 0.08 }, 0.2)
        .from(root.querySelectorAll('.px-colhero__pill'), { y: 16, autoAlpha: 0, duration: 0.8, ease: 'power3.out', stagger: 0.05 }, 0.5)
        .from(root.querySelector('.px-colhero__ring'), { scale: 0, autoAlpha: 0, duration: 1.4, ease: 'expo.out' }, 0.6);
      root.querySelectorAll('[data-px-split]').forEach(revealHeading);
      introDone.then(() => tl.play());
    },

    // About: the portrait unrolls from the top like a scroll, the stamp rolls
    // in; on desktop the chapters travel sideways while the page is pinned.
    about(root) {
      const portrait = root.querySelector('.px-about__portrait');
      const intro = root.querySelectorAll('.px-about__intro > *');
      const tl = gsap.timeline({ paused: true });
      tl.from(intro, { autoAlpha: 0, x: rtl ? 40 : -40, duration: 1, ease: 'power3.out', stagger: 0.12 }, 0);
      if (portrait) {
        tl.fromTo(portrait, { clipPath: 'inset(0% 0% 100% 0% round 50vw 50vw 1.2rem 1.2rem)' }, { clipPath: 'inset(0% 0% 0% 0% round 50vw 50vw 1.2rem 1.2rem)', duration: 1.6, ease: 'power4.inOut' }, 0.1)
          .from(portrait.querySelector('img, svg'), { yPercent: -12, duration: 1.8, ease: 'power3.out' }, 0.1)
          .from(root.querySelector('.px-about__stamp'), { xPercent: -160, rotation: -300, autoAlpha: 0, duration: 1.4, ease: 'power3.out' }, 0.9);
      }
      introDone.then(() => tl.play());

      const chapters = root.querySelector('.px-about__chapters');
      const track = root.querySelector('.px-about__track');
      if (chapters && track) {
        gsap.from(root.querySelectorAll('.px-chapter__no'), { yPercent: 100, autoAlpha: 0, duration: 1, ease: 'power3.out', stagger: 0.1, delay: 0.3, scrollTrigger: once(chapters, 'top 85%') });
        gsap.from(root.querySelectorAll('.px-chapter'), { autoAlpha: 0, x: 60, duration: 1, ease: 'power3.out', stagger: 0.12, scrollTrigger: once(chapters, 'top 85%') });
      }
      const values = root.querySelectorAll('.px-value');
      if (values.length) {
        gsap.from(values, { autoAlpha: 0, scale: 0.85, borderRadius: '50%', duration: 1.1, ease: 'power3.out', stagger: 0.12, scrollTrigger: once(root.querySelector('.px-about__values'), 'top 85%') });
      }
      const letter = root.querySelector('.px-about__letter');
      if (letter) gsap.from(letter.querySelector('.px-about__quote'), { scale: 3, autoAlpha: 0, duration: 1.2, ease: 'expo.out', scrollTrigger: once(letter, 'top 85%') });
    },

    // FAQ: the big question mark spins into place; each question's rule draws
    // across before its words arrive. Answers open with a measured height.
    // Search page: the lens swings in on its handle, the field unrolls from
    // it, chips are dealt onto a line and the category arches tip up.
    spage(root) {
      const tl = gsap.timeline({ paused: true });
      tl.from(root.querySelector('.px-shead__lens'), { rotation: 70, transformOrigin: '80% 80%', scale: 0.5, autoAlpha: 0, duration: 1.1, ease: 'elastic.out(1, 0.55)' }, 0)
        .from(root.querySelectorAll('.px-shead > :not(.px-shead__lens)'), { autoAlpha: 0, y: 20, duration: 0.8, ease: 'power3.out', stagger: 0.08 }, 0.15)
        .fromTo(root.querySelector('.template-search__search .field'), { clipPath: 'inset(0 50% 0 50% round 99rem)' }, { clipPath: 'inset(0 0% 0 0% round 99rem)', duration: 1, ease: 'power3.inOut', clearProps: 'clipPath' }, 0.35)
        .from(root.querySelectorAll('.px-sbrowse__none, .px-sbrowse__title'), { autoAlpha: 0, duration: 0.6, stagger: 0.1 }, 0.8)
        .from(root.querySelectorAll('.px-sbrowse__chip'), { autoAlpha: 0, x: rtl ? 40 : -40, duration: 0.6, ease: 'power3.out', stagger: 0.05 }, 0.9)
        .from(root.querySelectorAll('.px-sbrowse__cat'), { rotationX: -75, transformOrigin: '50% 100%', autoAlpha: 0, duration: 1, ease: 'power3.out', stagger: 0.08 }, 1.1);
      introDone.then(() => tl.play());
    },

    faq(root) {
      const tl = gsap.timeline({ paused: true });
      tl.from(root.querySelector('.px-faq__mark'), { rotation: -200, scale: 0.3, autoAlpha: 0, duration: 1.6, ease: 'back.out(1.6)' }, 0)
        .from(root.querySelectorAll('.px-faq__head > :not(.px-faq__mark)'), { autoAlpha: 0, y: 16, duration: 0.8, ease: 'power2.out', stagger: 0.08 }, 0.2);
      introDone.then(() => tl.play());
      const qs = Array.from(root.querySelectorAll('.px-q'));
      ScrollTrigger.batch(qs, {
        start: 'top 97%',
        once: true,
        onEnter: (batch, triggers) => batchRun(triggers, (tl) => {
          tl.fromTo(batch, { borderBottomColor: 'rgba(180, 138, 78,0)' }, { borderBottomColor: 'rgba(34, 12, 19,0.12)', duration: 0.8, stagger: 0.08, clearProps: 'borderBottomColor' }, 0)
            .from(batch.map((q) => q.querySelector('.px-q__summary')), { autoAlpha: 0, x: rtl ? -30 : 30, duration: 0.8, ease: 'power3.out', stagger: 0.08 }, 0.15);
        }),
      });
    },

    // Shipping: the gold route draws itself as you scroll and lights each stop.
    shipping(root) {
      root.querySelectorAll('.px-ship__head > *').forEach((el, i) => gsap.from(el, { autoAlpha: 0, y: 20, duration: 0.9, delay: 0.1 * i, ease: 'power2.out' }));
      const route = root.querySelector('.px-route');
      const path = root.querySelector('.px-route__draw');
      const stops = Array.from(root.querySelectorAll('.px-stop'));
      if (!route) return;
      if (path && path.getTotalLength) {
        const len = path.getTotalLength();
        gsap.set(path, { strokeDasharray: len, strokeDashoffset: len });
        gsap.to(path, { strokeDashoffset: 0, duration: 2.4, ease: 'power1.inOut', scrollTrigger: once(route, 'top 75%') });
      }
      stops.forEach((stop, i) => {
        ScrollTrigger.create({
          trigger: route,
          start: () => `top+=${(i / Math.max(stops.length, 1)) * route.offsetHeight * 0.6} 70%`,
          once: true,
          onEnter: () => stop.classList.add('is-lit'),
        });
      });
      gsap.from(stops.map((s) => s.querySelectorAll('.px-stop__no, .px-stop__title, .px-stop__text')), { autoAlpha: 0, y: 14, duration: 0.8, stagger: 0.05, ease: 'power2.out', scrollTrigger: once(route, 'top 80%') });
      const details = root.querySelector('.px-ship__details');
      if (details) gsap.from(details, { autoAlpha: 0, y: 40, rotation: rtl ? -1.5 : 1.5, duration: 1.1, ease: 'power3.out', scrollTrigger: once(details, 'top 88%') });
    },

    // Returns: the step cards start as one stacked deck and spread into place.
    returns(root) {
      root.querySelectorAll('.px-returns__head > *').forEach((el, i) => gsap.from(el, { autoAlpha: 0, y: 20, duration: 0.9, delay: 0.1 * i, ease: 'power2.out' }));
      const deck = root.querySelector('.px-deck');
      const cards = Array.from(root.querySelectorAll('.px-deck__card'));
      if (!deck || !cards.length) return;
      const first = cards[0].getBoundingClientRect();
      gsap.set(cards, {
        x: (i) => first.left - cards[i].getBoundingClientRect().left,
        y: (i) => first.top - cards[i].getBoundingClientRect().top,
        rotation: (i) => (i - (cards.length - 1) / 2) * 4,
        zIndex: (i) => cards.length - i,
        autoAlpha: 0,
      });
      gsap.timeline({ scrollTrigger: once(deck, 'top 80%') })
        .to(cards, { autoAlpha: 1, duration: 0.35, stagger: 0.04 })
        .to(cards, { x: 0, y: 0, rotation: 0, duration: 1.2, ease: 'power3.inOut', stagger: 0.1, clearProps: 'transform,zIndex' }, 0.4);
    },

    // Care: cards rise and a sweep of light passes over each, like a polish.
    care(root) {
      root.querySelectorAll('.px-care__head > *').forEach((el, i) => gsap.from(el, { autoAlpha: 0, y: 20, duration: 0.9, delay: 0.1 * i, ease: 'power2.out' }));
      root.querySelectorAll('.px-care__col').forEach((col) => {
        const cards = col.querySelectorAll('.px-care-card');
        gsap.timeline({ scrollTrigger: once(col, 'top 85%') })
          .from(col.querySelector('.px-care__badge'), { scale: 0, duration: 0.8, ease: 'back.out(3)' }, 0)
          .from(cards, { autoAlpha: 0, y: 30, duration: 0.8, ease: 'power3.out', stagger: 0.1 }, 0.1)
          .add(() => cards.forEach((card, i) => setTimeout(() => card.classList.add('is-shining'), i * 140)), 0.5);
      });
    },

    // Any other page: the title's letters drop in and settle with a bounce.
    read(root) {
      const title = root.querySelector('.px-read__title');
      const tl = gsap.timeline({ paused: true });
      if (title && window.SplitText) {
        const unit = textUnit('chars');
        const split = SplitText.create(title, { type: unit });
        tl.from(split[unit], { y: -50, autoAlpha: 0, duration: 0.9, ease: 'bounce.out', stagger: 0.025 }, 0);
      }
      tl.from(root.querySelector('.px-read__rule'), { scaleX: 0, duration: 1, ease: 'expo.out' }, 0.4)
        .from(root.querySelectorAll('.px-read__crumbs, .px-read__content'), { autoAlpha: 0, duration: 1, stagger: 0.2 }, 0.5);
      introDone.then(() => tl.play());
    },

    // Contact: the envelope's flap folds open and the letter (the form) rises out.
    // Contact: the side rises in, the gold rule draws down between the two
    // halves, and the form's lines are ruled one after another as if on paper.
    contact(root) {
      const tl = gsap.timeline({ paused: true });
      tl.from(root.querySelectorAll('.px-ct__side > :not(.px-ct__ways)'), { y: 24, autoAlpha: 0, duration: 0.9, ease: 'power3.out', stagger: 0.08 }, 0)
        .from(root.querySelectorAll('.px-ct__way'), { y: 18, autoAlpha: 0, duration: 0.8, ease: 'power3.out', stagger: 0.08 }, 0.35)
        .from(root.querySelector('.px-ct__rule'), { scaleY: 0, duration: 1.4, ease: 'power3.inOut' }, 0.2)
        .from(root.querySelectorAll('.px-ct__head > *'), { y: 18, autoAlpha: 0, duration: 0.8, ease: 'power3.out', stagger: 0.08 }, 0.4);
      const fields = Array.from(root.querySelectorAll('.px-ct__field'));
      fields.forEach((field, i) => {
        const line = field.querySelector('.px-ct__line');
        const at = 0.7 + i * 0.16;
        // The gold line rules across, then settles into the quiet grey underline.
        tl.fromTo(line, { scaleX: 0 }, { scaleX: 1, duration: 0.7, ease: 'power2.inOut' }, at)
          .to(line, { scaleX: 0, transformOrigin: rtl ? '0% 50%' : '100% 50%', duration: 0.6, ease: 'power2.in', clearProps: 'transform,transformOrigin' }, at + 0.75)
          .from(field.querySelector('.px-ct__label'), { y: 10, autoAlpha: 0, duration: 0.6, ease: 'power3.out' }, at);
      });
      tl.from(root.querySelector('.px-ct__send'), { y: 16, autoAlpha: 0, duration: 0.8, ease: 'power3.out' }, 0.7 + fields.length * 0.16);
      introDone.then(() => tl.play());
    },

    // 404: the two fours slide in from the sides, then the gem falls into the
    // gap and bounces until it settles.
    lost(root) {
      const fours = root.querySelectorAll('.px-404__four');
      const tl = gsap.timeline({ paused: true });
      tl.from(fours[0], { xPercent: rtl ? 120 : -120, autoAlpha: 0, duration: 0.9, ease: 'power3.out' }, 0)
        .from(fours[1], { xPercent: rtl ? -120 : 120, autoAlpha: 0, duration: 0.9, ease: 'power3.out' }, 0)
        .from(root.querySelector('.px-404__gem'), { y: -520, rotation: -200, duration: 1.3, ease: 'bounce.out' }, 0.45)
        .from(root.querySelectorAll('.px-404__title, .px-404__text, .px-404__search, .px-404__links, .px-404__home'), { autoAlpha: 0, y: 16, duration: 0.7, stagger: 0.08, ease: 'power2.out' }, 1.2);
      introDone.then(() => tl.play());
    },

    // Collection banner: the photo opens like venetian blinds, each word of
    // the name wipes in from the left; scrolling zooms the photo and lifts the text.
    cbanner(root) {
      const img = root.querySelector('.px-cbanner__img');
      const blinds = root.querySelectorAll('.px-cbanner__blinds span');
      const words = root.querySelectorAll('.px-cbanner__word');
      gsap.set(blinds, { scaleY: 1 });
      const tl = gsap.timeline({ paused: true });
      tl.to(blinds, { scaleY: 0, transformOrigin: '50% 0%', duration: 1.1, ease: 'power4.inOut', stagger: { each: 0.08, from: rtl ? 'end' : 'start' } }, 0)
        .fromTo(img, { scale: 1.18 }, { scale: 1.02, duration: 2.4, ease: 'power3.out' }, 0)
        .from(root.querySelectorAll('.px-cbanner__crumbs, .px-cbanner__count'), { autoAlpha: 0, x: rtl ? 30 : -30, duration: 0.9, ease: 'power3.out', stagger: 0.1 }, 0.55)
        .fromTo(words, { clipPath: rtl ? 'inset(0% 0% 0% 100%)' : 'inset(0% 100% 0% 0%)' }, { clipPath: OPEN, clearProps: 'clipPath', duration: 1, ease: 'power3.inOut', stagger: 0.14 }, 0.7)
        .from(root.querySelectorAll('.px-cbanner__desc, .px-cbanner__cue'), { autoAlpha: 0, y: 18, duration: 0.9, ease: 'power2.out', stagger: 0.12 }, 1.1);
      introDone.then(() => tl.play());
    },

    // Hang tags: the rail stretches out, threads unroll, then each tag drops
    // and swings on its thread until it settles.
    tags(root) {
      revealHead(root);
      const list = root.querySelector('.px-tags');
      const tags = Array.from(root.querySelectorAll('.px-tag'));
      if (!list || !tags.length) return;
      const threads = tags.map((t) => t.querySelector('.px-tag__thread'));
      const cards = tags.map((t) => t.querySelector('.px-tag__card'));
      gsap.set(threads, { scaleY: 0 });
      gsap.set(cards, { autoAlpha: 0, y: -60, rotation: (i) => (i % 2 ? 14 : -14) });
      const rail = root.querySelector('.px-tags__rail');
      if (rail) gsap.from(rail, { scaleX: 0, duration: 1.2, ease: 'expo.out', scrollTrigger: once(list, 'top 85%') });
      ScrollTrigger.batch(tags, {
        start: 'top 97%',
        once: true,
        onEnter: (batch, triggers) => batchRun(triggers, (tl) => {
          batch.forEach((tag, n) => {
            const i = tags.indexOf(tag);
            const sway = i % 2 ? 1 : -1;
            tl.add(gsap.timeline()
              .to(threads[i], { scaleY: 1, duration: 0.5, ease: 'power2.out' })
              .to(cards[i], { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.in' }, 0.2)
              .to(cards[i], {
                keyframes: { rotation: [14 * sway, -9 * sway, 5 * sway, -2.5 * sway, 1 * sway, 0] },
                duration: 1.8,
                ease: 'sine.out',
                clearProps: 'transform',
              }, 0.2), n * 0.12);
          });
        }),
      });
    },

    // All collections: each arch rises as its row reaches the screen.
    colindex(root) {
      revealHead(root);
      const cards = Array.from(root.querySelectorAll('.px-colgrid > li'));
      const media = (list) => list.map((c) => c.querySelector('.px-cat__media')).filter(Boolean);
      gsap.set(media(cards), { clipPath: 'inset(100% 0% 0% 0%)' });
      gsap.set(cards.flatMap((c) => Array.from(c.querySelectorAll('.px-cat__body > *'))), { y: 20, autoAlpha: 0 });
      ScrollTrigger.batch(cards, {
        start: 'top 97%',
        once: true,
        onEnter: (batch, triggers) => batchRun(triggers, (tl) => {
          const imgs = batch.map((c) => c.querySelector('.px-cat__media img, .px-cat__media svg')).filter(Boolean);
          const release = calm(imgs);
          tl.to(media(batch), { clipPath: OPEN, clearProps: 'clipPath', duration: 1.3, ease: 'expo.out', stagger: 0.1 }, 0)
            .fromTo(imgs, { scale: 1.3 }, { scale: 1, clearProps: 'transform', duration: 1.8, ease: 'expo.out', stagger: 0.1, onComplete: release }, 0)
            .to(batch.flatMap((c) => Array.from(c.querySelectorAll('.px-cat__body > *'))), { y: 0, autoAlpha: 1, duration: 0.8, ease: 'power3.out', stagger: 0.04 }, 0.35);
        }),
      });
    },

    categories(root) {
      revealHead(root);
      // Each arch rises out of its gold frame, then its label settles in.
      // Cards stay on one baseline (no scroll drift) so the row reads cleanly.
      const frames = root.querySelectorAll('.px-cat__frame');
      const images = root.querySelectorAll('.px-cat__media img, .px-cat__media svg');
      const release = calm([...frames, ...images]);
      const tl = gsap.timeline({ scrollTrigger: once(root.querySelector('.px-track'), 'top 85%'), onComplete: release });
      tl.fromTo(frames, { borderColor: 'rgba(180, 138, 78,0)' }, { borderColor: 'rgba(180, 138, 78,0.3)', clearProps: 'borderColor', duration: 1.2, stagger: 0.08 }, 0)
        .fromTo(root.querySelectorAll('.px-cat__media'), { clipPath: 'inset(100% 0% 0% 0%)' }, {
          clipPath: OPEN, clearProps: 'clipPath', duration: 1.3, ease: 'expo.out', stagger: 0.08,
        }, 0.1)
        .fromTo(images, { scale: 1.35 }, { scale: 1, clearProps: 'transform', duration: 1.8, ease: 'expo.out', stagger: 0.08 }, 0.1)
        .from(root.querySelectorAll('.px-cat__body > *'), { y: 20, autoAlpha: 0, duration: 0.8, ease: 'power3.out', stagger: 0.03 }, 0.45)
        .from(root.querySelectorAll('.px-section-foot > *'), { autoAlpha: 0, y: 20, duration: 0.8, stagger: 0.1 }, 0.7);
    },

    tabs(root) {
      revealHead(root);
      revealSoft(root.querySelectorAll('.px-tabs__tab'), root.querySelector('.px-tabs__list'), { y: 16, stagger: 0.1 });
      const items = root.querySelectorAll('[role="tabpanel"]:not([hidden]) .grid__item');
      gsap.set(items, { y: 70, autoAlpha: 0, scale: 0.97 });
      ScrollTrigger.batch(items, {
        start: 'top 97%',
        once: true,
        onEnter: (batch, triggers) => batchPace(gsap.to(batch, { y: 0, autoAlpha: 1, scale: 1, duration: 1.2, ease: 'expo.out', stagger: 0.1 }), triggers),
      });
      revealSoft(root.querySelectorAll('.px-center'), root.querySelector('.px-center'));
    },

    tiles(root) {
      revealHead(root);
      // Tiles open with a diagonal wipe from the top corner (clip-path only, no
      // 3D) while the photo turns level. The tile element itself is never
      // transformed, so hovering mid-animation can't make it jump.
      const tiles = Array.from(root.querySelectorAll('.px-tile'));
      const images = root.querySelectorAll('.px-tile__media img, .px-tile__media svg');
      const release = calm(images);
      const trigger = once(root.querySelector('.px-tiles'), 'top 82%');
      // Each tile sits under a square cover turned 45deg; sliding it along the
      // diagonal sweeps a slanted edge from the top-left corner to the
      // bottom-right one. Sizes are read once, here, before anything is written.
      const sizes = tiles.map((tile) => [tile.offsetWidth, tile.offsetHeight]);
      const covers = tiles.map((tile) => addCover(tile));
      const sweep = sizes.map(([w, h]) => Math.hypot(w, h));
      covers.forEach((cover, i) => {
        const [w, h] = sizes[i];
        const side = sweep[i] * 2;
        const start = sweep[i] / 2 / Math.SQRT2;
        cover.style.inset = 'auto';
        gsap.set(cover, { width: side, height: side, left: (w - side) / 2, top: (h - side) / 2, rotation: 45, x: rtl ? -start : start, y: start });
      });
      const tl = gsap.timeline({ scrollTrigger: trigger, onComplete: () => { release(); covers.forEach((c) => c.remove()); } })
        .to(covers, {
          x: (i) => (rtl ? -1 : 1) * (sweep[i] * 1.5) / Math.SQRT2,
          y: (i) => (sweep[i] * 1.5) / Math.SQRT2,
          duration: 1.1, ease: 'power3.inOut', stagger: 0.1,
        }, 0)
        .fromTo(images, { scale: 1.12, rotation: -3 }, { scale: 1, rotation: 0, duration: 1.4, ease: 'power3.out', stagger: 0.1 }, 0.1)
        .from(root.querySelectorAll('.px-tile__body > *'), { y: 30, autoAlpha: 0, duration: 0.8, ease: 'power3.out', stagger: 0.05 }, 0.5);
      // Price figures rise letter by letter as part of the same entrance.
      root.querySelectorAll('.px-tile--price .px-tile__title').forEach((title, i) => {
        if (!window.SplitText) return;
        const unit = textUnit('chars');
        const s = SplitText.create(title, { type: unit, mask: unit });
        tl.from(s[unit], { yPercent: 100, duration: 0.8, ease: 'expo.out', stagger: 0.03 }, 0.55 + i * 0.1);
      });
    },

    countdown(root) {
      const banner = root.querySelector('.px-deal__banner');
      const tl = gsap.timeline({ scrollTrigger: once(banner, 'top 80%') });
      tl.fromTo(banner, { clipPath: 'inset(0% 100% 0% 0%)' }, { clipPath: OPEN, clearProps: 'clipPath', duration: 1.6, ease: 'power3.inOut' })
        .from(banner.querySelector('.px-deal__banner-media'), { scale: 1.3, duration: 2, ease: 'expo.out' }, 0.2)
        .from(banner.querySelectorAll('.px-deal__content > :not(.px-countdown)'), { y: 40, autoAlpha: 0, duration: 1, ease: 'power3.out', stagger: 0.08 }, 0.8)
        .from(banner.querySelectorAll('.px-countdown__unit'), {
          rotationX: -100, autoAlpha: 0, transformOrigin: '50% 0%', transformPerspective: 600,
          duration: 1, ease: 'back.out(1.6)', stagger: 0.1,
        }, 1);
      const products = root.querySelector('.px-deal__products');
      gsap.from(products.querySelectorAll('.px-head > *'), { autoAlpha: 0, y: 20, duration: 0.8, stagger: 0.1, scrollTrigger: once(products, 'top 85%') });
      gsap.from(products.querySelectorAll('.grid__item'), {
        x: 120, autoAlpha: 0, duration: 1.4, ease: 'expo.out', stagger: 0.12, scrollTrigger: once(products, 'top 80%'),
      });
    },

    story(root) {
      const main = root.querySelector('.px-story__main');
      const accent = root.querySelector('.px-story__accent');
      const third = root.querySelector('.px-story__third');
      const badge = root.querySelector('.px-story__badge');
      const story = root.querySelector('.px-story');
      // First image opens when the section arrives.
      unclip(main, 'inset(0% 100% 0% 0%)', { duration: 1.8, ease: 'power3.inOut', scrollTrigger: once(main, 'top 80%') });
      gsap.from(main.querySelector('img, svg'), { scale: 1.35, duration: 2.4, ease: 'expo.out', scrollTrigger: once(main, 'top 80%') });
      if (badge) {
        gsap.from(badge, { scale: 0, autoAlpha: 0, duration: 1.2, ease: 'back.out(1.4)', delay: 0.4, scrollTrigger: once(main, 'top 80%') });
        if (!accent && !third) badgeInk(main, badge).then((c) => gsap.set(badge, { color: c }));
      }
      // Then the scroll lets the second photo take over the card from its
      // bottom-right corner, and the third from its bottom-left corner. The section (desktop) or
      // just the photos (phones) hold still below the header while this happens.
      const extras = [accent, third].filter(Boolean);
      if (extras.length) {
        // 2nd photo grows out of the bottom-right corner, 3rd out of the bottom-left.
        gsap.set(accent, { clipPath: 'inset(100% 0% 0% 100%)' });
        gsap.set(accent && accent.querySelector('img, svg'), { scale: 1.3 });
        gsap.set(third, { clipPath: 'inset(100% 100% 0% 0%)' });
        gsap.set(third && third.querySelector('img, svg'), { scale: 1.3 });
        const steps = gsap.timeline({ defaults: { ease: 'none' } });
        if (accent) {
          steps.to(accent, { clipPath: OPEN, duration: 1 }, 0)
            .to(accent.querySelector('img, svg'), { scale: 1, duration: 1 }, 0);
        }
        if (third) {
          steps.to(third, { clipPath: OPEN, duration: 1 }, accent ? 1.15 : 0)
            .to(third.querySelector('img, svg'), { scale: 1, duration: 1 }, accent ? 1.15 : 0);
        }
        steps.to({}, { duration: 0.3 });
        // The badge turns with the same scroll that changes the photos.
        if (badge) steps.fromTo(badge, { rotation: 0 }, { rotation: 300, duration: steps.duration() }, 0);
        // The badge ink follows the photo under it: gold on dark, espresso on light.
        if (badge) {
          const photos = [main, accent, third];
          Promise.all(photos.map((p) => p && badgeInk(p, badge))).then(([c0, c1, c2]) => {
            let ink = c0;
            gsap.set(badge, { color: ink });
            const shift = (to, at) => {
              if (!to || to === ink) return;
              steps.fromTo(badge, { color: ink }, { color: to, duration: 0.3, immediateRender: false }, at);
              ink = to;
            };
            if (accent) shift(c1, 0.6);
            if (third) shift(c2, (accent ? 1.15 : 0) + 0.1);
          });
        }
        const desktop = window.matchMedia('(min-width: 990px)').matches;
        ScrollTrigger.create({
          animation: steps,
          trigger: desktop ? story : root.querySelector('.px-story__visual'),
          // Hold just below the header so all three photos are fully in view.
          start: () => `top ${(parseInt(getComputedStyle(html).getPropertyValue('--px-header-h'), 10) || 100) + (desktop ? 24 : 12)}px`,
          end: desktop ? '+=110%' : '+=90%',
          // Desktop holds the whole section; phones hold just the photos.
          pin: desktop ? story : root.querySelector('.px-story__visual'),
          pinSpacing: true,
          scrub: 0.6,
          anticipatePin: 1,
        });
      }
      main.querySelectorAll('[data-px-parallax]').forEach((el) => el.removeAttribute('data-px-parallax'));
      revealHead(root);
      revealSoft(root.querySelectorAll('.px-story__body, .px-story__text > div:last-child'), root.querySelector('.px-story__text'), { delay: 0.15 });
      gsap.from(root.querySelectorAll('.px-stat'), {
        y: 40, autoAlpha: 0, duration: 1.1, ease: 'expo.out', stagger: 0.12, scrollTrigger: once(root.querySelector('.px-stats') || root, 'top 90%'),
      });
      gsap.from(root.querySelector('.px-stats'), { '--px-rule': 0, duration: 1.6, ease: 'power3.inOut', scrollTrigger: once(root.querySelector('.px-stats') || root, 'top 92%') });
      countUp(root);
    },

    lookbook(root) {
      const media = root.querySelector('.px-look__media');
      const pins = root.querySelectorAll('.px-hotspot__pin');
      const release = calm(pins);
      const tl = gsap.timeline({ scrollTrigger: once(media, 'top 78%'), onComplete: release });
      tl.fromTo(media, { clipPath: 'circle(0% at 50% 50%)' }, { clipPath: 'circle(75% at 50% 50%)', clearProps: 'clipPath', duration: 1.8, ease: 'power3.inOut' })
        .from(media.querySelector('img, svg'), { scale: 1.4, rotation: 3, duration: 2.2, ease: 'expo.out' }, 0)
        .fromTo(pins, { scale: 0 }, { scale: 1, clearProps: 'transform', duration: 0.9, ease: 'back.out(3)', stagger: 0.15 }, 1.1);
      revealHead(root);
      const products = root.querySelectorAll('.px-look__product');
      if (products.length) {
        gsap.from(products, {
          x: 60, autoAlpha: 0, duration: 1.1, ease: 'expo.out', stagger: 0.1, scrollTrigger: once(root.querySelector('.px-look__text'), 'top 75%'),
        });
      }
      revealSoft(root.querySelectorAll('.px-look__text > div:last-child'), root.querySelector('.px-look__text'), { delay: 0.25 });
    },

    occasions(root) {
      revealHead(root);
      const panels = root.querySelectorAll('.px-panel');
      calm(root.querySelectorAll('.px-panel__media img, .px-panel__media svg'));
      // Panels open upwards as a cover slides off (no squashing of the photos). Side by
      // side on desktop they open together; stacked on phones each opens as it arrives.
      const reveal = (panel, i) => {
        const pics = panel.querySelectorAll('.px-panel__media img, .px-panel__media svg');
        const tl = gsap.timeline({ delay: i * 0.12, onComplete: () => pics.forEach((el) => el.classList.remove('px-animating')) });
        const cover = panel.querySelector('.px-cover');
        tl.to(cover, { scaleY: 0, duration: 1.2, ease: 'power3.inOut', onComplete: () => cover && cover.remove() })
          .to(pics, { scale: 1, duration: 1.8, ease: 'expo.out' }, 0.15)
          .to(panel.querySelectorAll('.px-panel__body > *'), { y: 0, autoAlpha: 1, duration: 0.9, ease: 'power3.out', stagger: 0.05 }, 0.6);
        return tl;
      };
      // Starting poses are set now, while the page loads, so nothing has to be
      // measured in the middle of a scroll when the panels open.
      panels.forEach((panel) => gsap.set(addCover(panel), { transformOrigin: '50% 0%' }));
      gsap.set(root.querySelectorAll('.px-panel__media img, .px-panel__media svg'), { scale: 1.3 });
      gsap.set(root.querySelectorAll('.px-panel__body > *'), { y: 30, autoAlpha: 0 });
      if (window.matchMedia('(min-width: 990px)').matches) {
        ScrollTrigger.create({ ...once(root.querySelector('.px-panels'), 'top 78%'), onEnter: () => panels.forEach(reveal) });
      } else {
        panels.forEach((panel) => ScrollTrigger.create({ ...once(panel, 'top 85%'), onEnter: () => reveal(panel, 0) }));
      }
    },

    wear(root) {
      // The heading rises once; the photos and names follow the scroll (px-wear).
      revealHead(root);
    },

    testimonials(root) {
      revealHead(root);
      const cards = root.querySelectorAll('.px-quote');
      const release = calm(cards);
      gsap.fromTo(cards, { y: 140, rotation: (i) => (i % 2 ? 5 : -5), autoAlpha: 0, transformOrigin: '50% 100%' }, {
        y: 0, rotation: 0, autoAlpha: 1, clearProps: 'transform', duration: 1.5, ease: 'expo.out', stagger: 0.14,
        scrollTrigger: once(root.querySelector('.px-track'), 'top 85%'), onComplete: release,
      });
      gsap.from(root.querySelectorAll('.px-quote__mark'), {
        scale: 0, rotation: -45, duration: 1.2, ease: 'back.out(2)', stagger: 0.14, delay: 0.3, scrollTrigger: once(root.querySelector('.px-track'), 'top 85%'),
      });
      revealSoft(root.querySelectorAll('.px-section-foot > *'), root.querySelector('.px-section-foot'));
    },

    trust(root) {
      revealHead(root);
      const items = root.querySelectorAll('.px-trust__item');
      items.forEach((item, i) => {
        const strokes = item.querySelectorAll('.px-trust__icon svg path, .px-trust__icon svg rect, .px-trust__icon svg circle');
        strokes.forEach((path) => {
          const len = typeof path.getTotalLength === 'function' ? path.getTotalLength() : 80;
          gsap.set(path, { strokeDasharray: len, strokeDashoffset: len });
        });
        const tl = gsap.timeline({ delay: i * 0.12, scrollTrigger: once(root.querySelector('.px-trust'), 'top 88%') });
        tl.from(item.querySelector('.px-trust__icon'), { scale: 0, duration: 1, ease: 'elastic.out(1, 0.6)' })
          .to(strokes, { strokeDashoffset: 0, duration: 1.4, ease: 'power2.inOut', stagger: 0.08 }, 0.2)
          .from(item.querySelectorAll('.px-trust__title, .px-trust__text'), { y: 16, autoAlpha: 0, duration: 0.8, stagger: 0.08 }, 0.4);
      });
    },

    instagram(root) {
      revealHead(root);
      gsap.fromTo(
        root.querySelectorAll('.px-insta__item'),
        { scale: 0.6, autoAlpha: 0, clipPath: 'inset(20% 20% 20% 20%)' },
        {
          scale: 1, autoAlpha: 1, clipPath: OPEN, clearProps: 'clipPath,transform', duration: 1.3, ease: 'expo.out',
          stagger: { each: 0.08, from: 'random' }, scrollTrigger: once(root.querySelector('.px-insta'), 'top 88%'),
        }
      );
    },

    // The last section lifts away like a drawer front and the footer opens
    // beneath it: rounded lip + shadow on the section, footer content rising
    // from underneath, a shade lifting off, and the wordmark surfacing.
    footer(root) {
      const group = root.closest('.shopify-section');
      const sections = Array.from(document.querySelectorAll('#MainContent > .shopify-section')).filter((s) => s.offsetHeight > 0);
      const last = sections[sections.length - 1];
      if (root.dataset.drawer === 'true' && group && last) {
        group.classList.add('px-drawer-on');
        last.classList.add('px-lip');
      }
      const opening = once(root, 'top 92%');
      const box = root.querySelector('.px-box');

      // Jewellery box: pinned while the clasp turns, the lid swings open on its
      // hinge, light spills out and the piece rises from the velvet.
      if (box) {
        const scene = box.querySelector('.px-box__scene');
        const copy = box.querySelectorAll('.px-box__copy > *');
        gsap.set(box, { '--lid': '0deg', '--glow': 0, '--rise': 0, '--spark': 0, '--clasp': '0deg', '--clasp-y': '0rem' });
        gsap.set(copy, { autoAlpha: 0, y: 40 });
        gsap
          .timeline({
            scrollTrigger: once(box.querySelector('.px-box__stage'), 'top 65%'),
          })
          .timeScale(0.4)
          .fromTo(scene, { scale: 0.8, rotationX: 38, y: 80 }, { scale: 1, rotationX: 14, y: 0, duration: 0.24, ease: 'power2.out' }, 0)
          .to(box, { '--clasp': '-18deg', '--clasp-y': '0.8rem', duration: 0.08, ease: 'back.out(3)' }, 0.2)
          .to(box, { '--lid': '-158deg', duration: 0.4, ease: 'power2.inOut' }, 0.26)
          .to(box, { '--glow': 1, duration: 0.28, ease: 'power1.out' }, 0.42)
          .to(box, { '--rise': 1, duration: 0.32, ease: 'power3.out' }, 0.5)
          .to(box, { '--spark': 1, duration: 0.18 }, 0.66)
          .to(copy, { autoAlpha: 1, y: 0, duration: 0.2, stagger: 0.05, ease: 'power3.out' }, 0.6)
          .to({}, { duration: 0.14 });
      }
      gsap.fromTo(root.querySelector('.px-footer__shade'), { opacity: 0.8 }, { opacity: 0, duration: 1.3, ease: 'power2.out', scrollTrigger: opening });
      gsap.fromTo(root, { '--px-handle': 0.15 }, { '--px-handle': 1, duration: 1.3, ease: 'power2.out', scrollTrigger: opening });

      // Consultation panel: the photo slides open, the frame corners draw in.
      const cta = root.querySelector('.px-footer__cta');
      if (cta) {
        const media = cta.querySelector('.px-footer__cta-media');
        const tl = gsap.timeline({ scrollTrigger: once(cta, 'top 85%') });
        tl.fromTo(media, { clipPath: 'inset(0% 100% 0% 0%)' }, { clipPath: OPEN, clearProps: 'clipPath', duration: 1.6, ease: 'power3.inOut' })
          .from(media.querySelector('img, svg'), { scale: 1.35, duration: 2.2, ease: 'expo.out' }, 0.2)
          .from(cta.querySelectorAll('.px-footer__cta-body > *'), { y: 40, autoAlpha: 0, duration: 1.1, ease: 'expo.out', stagger: 0.1 }, 0.6);
      }

      const cols = root.querySelectorAll('.px-footer__col');
      gsap.from(cols, { y: 50, autoAlpha: 0, duration: 1.2, ease: 'expo.out', stagger: 0.08, scrollTrigger: once(root.querySelector('.px-footer__grid'), 'top 92%') });

      // Promise strip: each line arrives with its gem spinning into place.
      const promises = root.querySelectorAll('.px-footer__promise');
      if (promises.length) {
        const trigger = once(root.querySelector('.px-footer__promises'), 'top 95%');
        gsap.from(promises, { y: 24, autoAlpha: 0, duration: 1, ease: 'expo.out', stagger: 0.1, scrollTrigger: trigger });
        gsap.from(root.querySelectorAll('.px-footer__gem'), { rotation: -180, scale: 0, duration: 1.2, ease: 'back.out(2)', stagger: 0.1, scrollTrigger: trigger });
      }

      // Wordmark: once it comes into view a pen draws each letter's thin gold
      // outline, letter by letter.
      const mark = root.querySelector('.px-footer__wordmark');
      if (mark && document.fonts) {
        document.fonts.ready.then(() => {
          const word = Array.from(mark.querySelectorAll('.px-footer__mark-char')).map((c) => c.textContent).join('').replace(/\u00a0/g, ' ').toUpperCase();
          if (!word.trim()) return;
          const ns = 'http://www.w3.org/2000/svg';
          const svg = document.createElementNS(ns, 'svg');
          svg.setAttribute('class', 'px-footer__svg');
          svg.setAttribute('aria-hidden', 'true');
          mark.appendChild(svg);
          mark.classList.add('has-svg');
          // Lay the word out once to find where each letter starts.
          const probe = document.createElementNS(ns, 'text');
          probe.setAttribute('y', '100');
          probe.textContent = word;
          svg.appendChild(probe);
          const box = probe.getBBox();
          const xs = Array.from(word, (_, i) => probe.getStartPositionOfChar(i).x);
          probe.remove();
          svg.setAttribute('viewBox', `${box.x - 3} ${box.y - 3} ${box.width + 6} ${box.height + 6}`);
          const letters = [];
          Array.from(word).forEach((ch, i) => {
            if (ch === ' ') return;
            const t = document.createElementNS(ns, 'text');
            t.setAttribute('x', xs[i].toFixed(2));
            t.setAttribute('y', '100');
            t.textContent = ch;
            svg.appendChild(t);
            letters.push(t);
          });
          // Keep the line 1px on screen whatever size the word is drawn at.
          const vbWidth = box.width + 6;
          const hairline = () => svg.style.setProperty('--px-mark-stroke', (vbWidth / Math.max(svg.clientWidth, 1)).toFixed(3));
          hairline();
          window.addEventListener('resize', hairline, { passive: true });
          if (reduced) return;
          gsap.set(letters, { strokeDasharray: 1400, strokeDashoffset: 1400 });
          gsap.to(letters, { strokeDashoffset: 0, duration: 1.8, ease: 'power1.inOut', stagger: 0.22, scrollTrigger: once(mark, 'top 90%') });
          ScrollTrigger.refresh();
        });
      }

      // Golden spotlight follows the pointer (phones get a slow CSS drift instead).
      if (root.dataset.spotlight === 'true' && finePointer) {
        const pos = { x: 50, y: 35 };
        const apply = () => {
          root.style.setProperty('--mx', `${pos.x}%`);
          root.style.setProperty('--my', `${pos.y}%`);
        };
        const xTo = gsap.quickTo(pos, 'x', { duration: 0.9, ease: 'power3.out', onUpdate: apply });
        const yTo = gsap.quickTo(pos, 'y', { duration: 0.9, ease: 'power3.out', onUpdate: apply });
        root.addEventListener('pointermove', (event) => {
          const r = root.getBoundingClientRect();
          xTo(((event.clientX - r.left) / r.width) * 100);
          yTo(((event.clientY - r.top) / r.height) * 100);
        });
      }

      // Back-to-top ring shows how much of the page has been read.
      const top = root.querySelector('.px-footer__top');
      if (top) {
        ScrollTrigger.create({
          start: 0, end: 'max',
          onUpdate: (self) => top.style.setProperty('--px-progress', self.progress.toFixed(3)),
        });
      }

      // Newsletter: a burst of gold sparks once the "thanks" message is in view.
      const thanks = root.querySelector('[data-px-celebrate]');
      if (thanks) {
        ScrollTrigger.create({ trigger: thanks, start: 'top 90%', once: true, onEnter: () => sparkBurst(thanks, 36, 160) });
      }
      gsap.from(root.querySelectorAll('.px-footer__bottom > *'), {
        y: 20, autoAlpha: 0, duration: 1, ease: 'power3.out', stagger: 0.08, scrollTrigger: once(root.querySelector('.px-footer__bottom'), 'top 98%'),
      });
    },

    // Dawn sections and pages ----------------------------------------------
    dawn(root) {
      const grid = root.querySelectorAll(
        '.product-grid > .grid__item, .collection-list > .grid__item, .blog-articles > *, .article-card-wrapper, .multicolumn-list > li'
      );
      if (grid.length) {
        // Product grids deal in like cards from a fanned hand.
        gsap.set(grid, { y: 40, autoAlpha: 0, rotation: (i) => (i % 2 ? 4 : -4), transformOrigin: '50% 100%' });
        ScrollTrigger.batch(grid, {
          start: 'top 97%', once: true,
          onEnter: (batch, triggers) => batchPace(gsap.to(batch, { y: 0, rotation: 0, autoAlpha: 1, duration: 1, ease: 'back.out(1.4)', stagger: 0.09, clearProps: 'transform' }), triggers),
        });
      }
      root.querySelectorAll('.title-wrapper h2, .title-wrapper-with-link h2, .collection-hero__title, .main-page-title, .newsletter__wrapper h2').forEach((el) => {
        el.dataset.pxSplit = el.dataset.pxSplit || 'blur';
        revealHeading(el);
      });
      // Product page: the photos turn in like pages of a book, thumbnails
      // surface one by one, the title rises out of a slant and the details unfold.
      const media = root.querySelectorAll('.product__media-list > .product__media-item');
      if (media.length) {
        gsap.fromTo(media, { rotationY: rtl ? 70 : -70, autoAlpha: 0, transformOrigin: rtl ? '100% 50%' : '0% 50%', transformPerspective: 1400 },
          { rotationY: 0, autoAlpha: 1, duration: 1.3, ease: 'power3.out', stagger: 0.12, delay: 0.15, clearProps: 'transform' });
      }
      // Cart page: rows and totals print out like a till receipt, in steps.
      const receipt = root.querySelectorAll('#main-cart-items .cart-item, .cart__footer');
      if (receipt.length) {
        gsap.fromTo(receipt, { clipPath: 'inset(0% 0% 100% 0%)', autoAlpha: 1 }, { clipPath: OPEN, autoAlpha: 1, clearProps: 'clipPath', duration: 0.9, ease: 'steps(9)', stagger: 0.18, delay: 0.25 });
      }
      const thumbs = root.querySelectorAll('.thumbnail-list__item');
      if (thumbs.length) gsap.from(thumbs, { autoAlpha: 0, scale: 0.6, duration: 0.7, ease: 'back.out(2)', stagger: 0.06, delay: 0.7, clearProps: 'transform' });
      const title = root.querySelector('.product__title');
      if (title) gsap.from(title, { yPercent: 40, skewY: 6, autoAlpha: 0, duration: 1.1, ease: 'power4.out', delay: 0.3, clearProps: 'transform' });
      const info = Array.from(root.querySelectorAll('.product__info-container > *')).filter((el) => !el.classList.contains('product__title'));
      if (info.length) gsap.from(info, { clipPath: 'inset(0% 0% 100% 0%)', autoAlpha: 0, duration: 0.9, ease: 'power2.out', stagger: 0.07, delay: 0.5, clearProps: 'clipPath' });
    },
  };

  /* ---------- Scroll animations ---------- */
  const contexts = new WeakMap();
  const fromVars = {
    up: { y: 60, autoAlpha: 0 },
    left: { x: -60, autoAlpha: 0 },
    right: { x: 60, autoAlpha: 0 },
    fade: { autoAlpha: 0 },
    zoom: { scale: 0.9, autoAlpha: 0 },
  };
  const outsideFx = (el) => !el.closest('[data-px-fx]');

  function initGeneric(scope) {
    scope.querySelectorAll('[data-px-reveal]').forEach((el) => {
      if (!outsideFx(el)) return;
      const type = el.dataset.pxReveal || 'up';
      const delay = parseFloat(el.dataset.pxDelay) || 0;
      if (type === 'mask') {
        gsap.set(el, { autoAlpha: 1 });
        gsap.fromTo(el, { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.4, delay, ease: 'expo.out', scrollTrigger: once(el, 'top 88%') });
        const inner = el.querySelector('img, video, svg');
        if (inner) gsap.fromTo(inner, { scale: 1.3 }, { scale: 1, duration: 1.8, delay, ease: 'expo.out', scrollTrigger: once(el, 'top 88%') });
        return;
      }
      gsap.fromTo(el, fromVars[type] || fromVars.up, { x: 0, y: 0, scale: 1, autoAlpha: 1, duration: 1.2, delay, ease: 'power3.out', scrollTrigger: once(el, 'top 88%') });
    });
    scope.querySelectorAll('[data-px-stagger]').forEach((group) => {
      if (!outsideFx(group)) return;
      const items = group.querySelectorAll('[data-px-item]');
      if (items.length) gsap.fromTo(items, { y: 50, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 1.1, ease: 'power3.out', stagger: 0.1, scrollTrigger: once(group, 'top 85%') });
    });
    scope.querySelectorAll('[data-px-split]').forEach((el) => outsideFx(el) && revealHeading(el));
    scope.querySelectorAll('[data-px-count]').forEach((el) => outsideFx(el) && countUp(el.parentElement));
  }

  function initHeroScroll(scope) {
    // The hero stays put while scrolling: motion that scrubs with the scroll
    // replays every time someone scrolls back, so the store has none.
    void scope;
  }

  function initScope(scope) {
    if (!motion) return;
    const ctx = gsap.context(() => {
      const fxRoots = scope.matches && scope.matches('[data-px-fx]') ? [scope] : Array.from(scope.querySelectorAll('[data-px-fx]'));
      fxRoots.forEach((root) => {
        try {
          (fx[root.dataset.pxFx] || fx.dawn)(root);
        } catch (error) {
          console.warn('[Parisaa] animation skipped:', root.dataset.pxFx, error);
          gsap.set(root.querySelectorAll('*'), { clearProps: 'all' });
        }
        root.classList.add('px-fx-ready');
      });
      if (!scope.querySelector('[data-px-fx]') && !scope.querySelector('.px-hero')) fx.dawn(scope);
      initGeneric(scope);
      initHeroScroll(scope);
    }, scope);
    contexts.set(scope, ctx);
  }

  function initMagnetic(scope) {
    if (!config.magnetic || !finePointer || !hasGsap || reduced) return;
    scope.querySelectorAll('[data-px-magnetic]').forEach((el) => {
      if (el.dataset.pxMagneticBound) return;
      el.dataset.pxMagneticBound = '1';
      const xTo = gsap.quickTo(el, 'x', { duration: 0.5, ease: 'power3.out' });
      const yTo = gsap.quickTo(el, 'y', { duration: 0.5, ease: 'power3.out' });
      el.addEventListener('pointermove', (event) => {
        const rect = el.getBoundingClientRect();
        xTo((event.clientX - rect.left - rect.width / 2) * 0.25);
        yTo((event.clientY - rect.top - rect.height / 2) * 0.35);
      });
      el.addEventListener('pointerleave', () => {
        xTo(0);
        yTo(0);
      });
    });
  }

  function init(scope) {
    initScope(scope);
    initMagnetic(scope);
  }

  // Split text only once the web fonts are in, so lines break where they will
  // finally sit. Sections stay in their hidden pose meanwhile (max ~1.2s).
  html.classList.add('px-ready');
  const fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
  const fontsOrTimeout = Promise.race([fontsReady, new Promise((r) => setTimeout(r, 1200))]);
  const ready = motion ? fontsOrTimeout : Promise.resolve();
  Promise.all([ready, written]).then(() => {
    document.querySelectorAll('#MainContent > .shopify-section, .shopify-section-group-footer-group').forEach(init);
    html.classList.add('px-booted');
    if (hasST) ScrollTrigger.refresh();
    markBooted();
  });

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    if (hasST) ScrollTrigger.refresh();
  });
  document.addEventListener('shopify:section:unload', (event) => {
    const ctx = contexts.get(event.target);
    if (ctx) ctx.revert();
  });
  if (hasST) {
    // (Not while the loader is writing the name: see `written`.)
    window.addEventListener('load', () => written.then(() => ScrollTrigger.refresh()));
    if (document.fonts && document.fonts.ready) Promise.all([document.fonts.ready, written]).then(() => ScrollTrigger.refresh());
    // Lazy images keep arriving while people scroll. A refresh measures every
    // trigger again (a long task), so only do it when the page really grew.
    let resizeTimer;
    let pageHeight = document.documentElement.scrollHeight;
    document.addEventListener('load', (event) => {
      if (event.target.tagName !== 'IMG') return;
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const height = document.documentElement.scrollHeight;
        if (Math.abs(height - pageHeight) < 2) return;
        pageHeight = height;
        written.then(() => ScrollTrigger.refresh());
      }, 300);
    }, true);
    ScrollTrigger.addEventListener('refresh', () => (pageHeight = document.documentElement.scrollHeight));
  }

  // Nothing animates where nobody can see it: CSS loops in the footer,
  // marquees and the hero (plus its autoplay bar) rest while off screen.
  if ('IntersectionObserver' in window) {
    const sleeper = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        target.classList.toggle('px-paused', !isIntersecting);
        if (target.progress) isIntersecting ? target.progress.resume() : target.progress.pause();
      });
    }, { rootMargin: '120px 0px' });
    const watchLoops = () => document.querySelectorAll('.px-footer, .px-marquee, px-hero, .px-cbanner').forEach((el) => sleeper.observe(el));
    watchLoops();
    document.addEventListener('shopify:section:load', watchLoops);
  }

  /* ---------- Components ---------- */

  class PxHero extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.px-hero__slide'));
      this.dots = Array.from(this.querySelectorAll('.px-hero__dot, .px-hero__thumb'));
      this.index = 0;
      this.duration = parseFloat(this.dataset.speed) || 6;
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1 && !reduced && !designMode;
      this.splits = new Map();

      const prev = this.querySelector('[data-px-prev]');
      const next = this.querySelector('[data-px-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
      if (next) next.addEventListener('click', () => this.go(this.index + 1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
      this.addEventListener('mouseenter', () => this.progress && this.progress.pause());
      this.addEventListener('mouseleave', () => this.progress && this.progress.resume());
      this.addEventListener('shopify:block:select', (event) => {
        const i = this.slides.indexOf(event.target);
        if (i > -1) this.go(i, true);
      });

      Promise.all([introDone, ready]).then(() => {
        this.entrance();
        this.play();
      });
    }

    disconnectedCallback() {
      if (this.progress) this.progress.kill();
    }

    // First impression: the photo is simply there and the words rise onto it.
    // (No curtain opening from the centre: the photo never shows cut down.)
    entrance() {
      const slide = this.slides[0];
      if (!slide || !motion) return this.introduce(slide);
      // Under the loader's sheet the photo and the thumbnails can settle in as
      // the sheet comes off. Without the loader they are on screen from the
      // first paint, so they stay exactly as they are.
      if (heroUnveiled) {
        gsap.fromTo(slide.querySelector('.px-hero__media'), { scale: 1.05 }, { scale: 1, duration: 2.2, ease: 'power3.out', clearProps: 'transform' });
        gsap.from(this.querySelector('.px-hero__ui'), { autoAlpha: 0, y: 20, duration: 1, ease: 'power3.out', delay: 0.5 });
      }
      this.introduce(slide, heroUnveiled ? 0.3 : 0);
    }

    play() {
      if (!this.autoplay || !hasGsap) return;
      if (this.progress) this.progress.kill();
      this.dots.forEach((dot) => gsap.set(dot.querySelector('.px-hero__dot-bar'), { scaleX: 0 }));
      const bar = this.dots[this.index] && this.dots[this.index].querySelector('.px-hero__dot-bar');
      this.progress = gsap.fromTo(bar || {}, { scaleX: 0 }, { scaleX: 1, duration: this.duration, ease: 'none', onComplete: () => this.go(this.index + 1) });
    }

    updateUI() {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.index);
        dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
      });
      const current = this.querySelector('[data-px-current]');
      if (current) {
        const value = String(this.index + 1).padStart(2, '0');
        if (motion) {
          gsap.timeline()
            .to(current, { yPercent: -100, autoAlpha: 0, duration: 0.35, ease: 'power2.in' })
            .add(() => (current.textContent = value))
            .fromTo(current, { yPercent: 100, autoAlpha: 0 }, { yPercent: 0, autoAlpha: 1, duration: 0.5, ease: 'power3.out' });
        } else current.textContent = value;
      }
    }

    go(target, instant = false) {
      const count = this.slides.length;
      const n = (target + count) % count;
      if (n === this.index || this.busy) return;
      const prev = this.slides[this.index];
      const next = this.slides[n];
      this.index = n;
      this.updateUI();

      if (!hasGsap || instant || !motion) {
        prev.classList.remove('is-active');
        next.classList.add('is-active');
        this.introduce(next);
        this.play();
        return;
      }

      // A soft cross-fade: the words of the old slide lift away, the new photo
      // fades in over it while settling from a slight zoom, and its words rise.
      // Only opacity and transform move, on layers that already exist.
      this.busy = true;
      next.classList.add('is-active', 'is-entering');
      gsap.set(next.querySelectorAll('.px-hero__inner > *'), { autoAlpha: 0 });
      gsap.fromTo(next.querySelector('.px-hero__media'), { scale: 1.08 }, { scale: 1, duration: 2.4, ease: 'power3.out', delay: 0.1, clearProps: 'transform' });
      gsap
        .timeline({
          onComplete: () => {
            prev.classList.remove('is-active');
            next.classList.remove('is-entering');
            gsap.set(prev.querySelectorAll('.px-hero__inner > *'), { clearProps: 'transform,opacity,visibility' });
            gsap.set(next, { clearProps: 'opacity,visibility' });
            this.busy = false;
            this.play();
          },
        })
        .to(prev.querySelectorAll('.px-hero__inner > *'), { y: -20, autoAlpha: 0, duration: 0.5, ease: 'power2.in', stagger: 0.03 }, 0)
        .fromTo(next, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1.2, ease: 'power2.inOut' }, 0.1)
        .add(() => this.introduce(next), 0.55);
    }

    // The slide's words: the small line and the text fade up, the heading's
    // words rise out of their own line masks one after another.
    introduce(slide, delay = 0) {
      if (!slide || !motion) return;
      const eyebrow = slide.querySelector('.px-hero__eyebrow');
      const rest = slide.querySelectorAll('.px-hero__text, .px-hero__buttons');
      const title = slide.querySelector('.px-hero__title');
      const tl = gsap.timeline({ delay });
      if (eyebrow) tl.fromTo(eyebrow, { y: 16, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.9, ease: 'power3.out' }, 0);
      if (title) {
        tl.set(title, { autoAlpha: 1 }, 0);
        if (window.SplitText) {
          if (!this.splits.has(title)) this.splits.set(title, SplitText.create(title, { type: 'words', mask: 'words' }));
          tl.fromTo(this.splits.get(title).words, { yPercent: 110 }, { yPercent: 0, duration: 1.1, ease: 'power3.out', stagger: 0.06 }, 0.1);
        }
      }
      if (rest.length) tl.fromTo(rest, { y: 24, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.9, ease: 'power3.out', stagger: 0.1 }, 0.4);
    }
  }

  class PxCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-px-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-px-prev]');
      this.next = this.querySelector('[data-px-next]');
      // In right-to-left pages "next" scrolls towards the left (negative scrollLeft).
      // (Not `this.dir`: that is the element's built-in dir attribute, which turns 1 into ''.)
      this.flow = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      const step = () => Math.max(this.track.clientWidth * 0.8, 200) * this.flow;
      if (this.prev) this.prev.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (this.next) this.next.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });
      window.addEventListener('resize', () => this.update());
      this.update();
      if (finePointer) this.enableDrag();
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      const offset = Math.abs(this.track.scrollLeft);
      if (this.prev) this.prev.disabled = offset <= 2;
      if (this.next) this.next.disabled = offset >= max;
      this.classList.toggle('is-scrollable', max > 0);
      // For the scroll line under a row: how much of the row is in view, and how far along it is.
      const total = this.track.scrollWidth;
      this.style.setProperty('--px-seen', total ? Math.min(1, this.track.clientWidth / total).toFixed(4) : 1);
      this.style.setProperty('--px-along', max > 0 ? Math.min(1, offset / max).toFixed(4) : 0);
    }

    enableDrag() {
      let startX = 0;
      let startLeft = 0;
      let moved = false;
      let down = false;
      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse') return;
        down = true;
        moved = false;
        startX = event.clientX;
        startLeft = this.track.scrollLeft;
      });
      window.addEventListener('pointermove', (event) => {
        if (!down) return;
        const delta = event.clientX - startX;
        if (Math.abs(delta) > 5) {
          moved = true;
          this.track.classList.add('is-dragging');
        }
        this.track.scrollLeft = startLeft - delta;
      });
      window.addEventListener('pointerup', () => {
        down = false;
        this.track.classList.remove('is-dragging');
      });
      this.track.addEventListener(
        'click',
        (event) => {
          if (moved) {
            event.preventDefault();
            event.stopPropagation();
            moved = false;
          }
        },
        true
      );
    }
  }

  class PxTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          const n = (i + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.select(n);
          this.tabs[n].focus();
        });
      });
      this.addEventListener('shopify:block:select', (event) => {
        const i = this.tabs.indexOf(event.target) > -1 ? this.tabs.indexOf(event.target) : this.panels.indexOf(event.target);
        if (i > -1) this.select(i);
      });
    }

    select(index) {
      const current = this.panels.find((panel) => !panel.hidden);
      const swap = () => {
        this.tabs.forEach((tab, i) => {
          tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
          tab.tabIndex = i === index ? 0 : -1;
        });
        this.panels.forEach((panel, i) => (panel.hidden = i !== index));
        const panel = this.panels[index];
        if (motion && panel) {
          gsap.fromTo(
            panel.querySelectorAll('.grid__item'),
            { y: 40, autoAlpha: 0, rotationY: -12, transformPerspective: 1000 },
            { y: 0, autoAlpha: 1, rotationY: 0, duration: 1, ease: 'expo.out', stagger: 0.07 }
          );
        }
        if (hasST) ScrollTrigger.refresh();
      };
      if (motion && current && current !== this.panels[index]) {
        gsap.to(current.querySelectorAll('.grid__item'), { y: -20, autoAlpha: 0, duration: 0.3, ease: 'power2.in', stagger: 0.03, onComplete: swap });
      } else swap();
    }
  }

  class PxCountdown extends HTMLElement {
    connectedCallback() {
      const raw = (this.dataset.end || '').trim().replace(' ', 'T');
      this.end = raw ? new Date(raw).getTime() : NaN;
      this.parts = {
        days: this.querySelector('[data-days]'),
        hours: this.querySelector('[data-hours]'),
        minutes: this.querySelector('[data-minutes]'),
        seconds: this.querySelector('[data-seconds]'),
      };
      if (isNaN(this.end)) return;
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const left = Math.max(0, this.end - Date.now());
      const pad = (n) => String(n).padStart(2, '0');
      const values = {
        days: Math.floor(left / 86400000),
        hours: Math.floor((left / 3600000) % 24),
        minutes: Math.floor((left / 60000) % 60),
        seconds: Math.floor((left / 1000) % 60),
      };
      Object.keys(values).forEach((key) => {
        const el = this.parts[key];
        if (!el) return;
        const value = pad(values[key]);
        if (el.textContent === value) return;
        el.textContent = value;
        if (motion && this.isConnected) gsap.fromTo(el, { yPercent: -40, autoAlpha: 0.2 }, { yPercent: 0, autoAlpha: 1, duration: 0.45, ease: 'power3.out' });
      });
      if (left === 0) {
        clearInterval(this.timer);
        this.classList.add('is-expired');
      }
    }
  }

  class PxHotspots extends HTMLElement {
    connectedCallback() {
      this.spots = Array.from(this.querySelectorAll('.px-hotspot'));
      this.spots.forEach((spot) => {
        const button = spot.querySelector('.px-hotspot__pin');
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          const open = button.getAttribute('aria-expanded') === 'true';
          this.closeAll();
          if (!open) button.setAttribute('aria-expanded', 'true');
        });
      });
      document.addEventListener('click', (event) => {
        if (!event.target.closest('.px-hotspot__card')) this.closeAll();
      });
      document.addEventListener('keydown', (event) => event.key === 'Escape' && this.closeAll());
      this.addEventListener('shopify:block:select', (event) => {
        this.closeAll();
        const pin = event.target.querySelector && event.target.querySelector('.px-hotspot__pin');
        if (pin) pin.setAttribute('aria-expanded', 'true');
      });
    }

    closeAll() {
      this.spots.forEach((spot) => spot.querySelector('.px-hotspot__pin').setAttribute('aria-expanded', 'false'));
    }
  }

  class PxStickyCart extends HTMLElement {
    connectedCallback() {
      const form = document.querySelector('product-form form[action*="/cart/add"]');
      const watch = document.querySelector('product-form .product-form__submit');
      if (!form || !watch || !('IntersectionObserver' in window)) return;
      const button = this.querySelector('[data-px-sticky-add]');
      if (button)
        button.addEventListener('click', () => {
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else watch.click();
        });
      new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', passed);
      }).observe(watch);
    }
  }

  /* ---------- Add to cart: the trolley, the button and the bag ---------- */
  const checkIcon =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true" focusable="false"><path d="M4 12.5l5 5L20 6.5"/></svg>';
  // A classic shopping cart: espresso frame, ivory wire basket, gold grip, deep-gold wheels.
  const trolleyIcon =
    '<svg viewBox="0 0 80 64" fill="none" stroke="#2a1e1b" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M20 14h56l-6 20H25z" fill="#fff" fill-opacity="0.72"/>' +
    '<path d="M32 14.5v19M44 14.5v19M56 14.5v19M67 14.5v19M21.7 20.7h52.3M23.3 27.3h48.6" stroke-width="1.3"/>' +
    '<path d="M12 8h5l9 32-2 7h44"/>' +
    '<path d="M3 8h9" stroke="#b48a4e" stroke-width="4.2"/>' +
    '<g class="px-fly__wheel"><circle cx="30" cy="54" r="5.2" fill="#8f6b3a" stroke-width="2"/><circle cx="30" cy="54" r="1.6" fill="#fff" stroke="none"/></g>' +
    '<g class="px-fly__wheel"><circle cx="63" cy="54" r="5.2" fill="#8f6b3a" stroke-width="2"/><circle cx="63" cy="54" r="1.6" fill="#fff" stroke="none"/></g>' +
    '</svg>';
  const bag = document.querySelector('cart-drawer');
  const flyOn = motion && hasGsap && config.atcEffect !== 'drawer' && !!bag;
  let lastAdd = null;
  let newVariant = null;
  let flight = null;
  let keepShut = false;
  let party = false;

  // Remember which button started the add, and send the trolley for it.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest && event.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      const button = form.querySelector('[type="submit"]');
      if (button && (button.disabled || button.getAttribute('aria-disabled') === 'true')) return;
      lastAdd = { button, at: Date.now() };
      if (flyOn && form.closest('product-form')) {
        if (flight) flight.drop();
        flight = launchTrolley(form, button);
      }
    },
    true
  );

  // While a trolley carries the piece, Dawn refreshes the bag but leaves it shut.
  if (flyOn) {
    customElements.whenDefined('cart-drawer').then(() => {
      const proto = customElements.get('cart-drawer').prototype;
      const open = proto.open;
      proto.open = function (trigger) {
        if (keepShut) {
          keepShut = false;
          return;
        }
        return open.call(this, trigger);
      };
    });
  }

  function imageFor(form) {
    // (.px-scard: the collection page's cards. Without it the add fell back to
    // the first image on the page and the trolley parked in the wrong place.)
    const scope = form.closest('quick-add-modal, .px-pcard, .px-scard, .card-wrapper, .card, product-info, .product') || document;
    const img =
      scope.querySelector('.px-pcard__img, .px-scard__photo img, .product__media-item.is-active img, .product__media img, .card__media img, .card__inner img') ||
      scope.querySelector('img');
    return img && img.getBoundingClientRect().width > 0 ? img : null;
  }

  function launchTrolley(form, button) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const dir = rtl ? -1 : 1;
    const layer = document.createElement('div');
    layer.className = 'px-fly';
    layer.setAttribute('aria-hidden', 'true');
    layer.innerHTML =
      '<div class="px-fly__cart"><div class="px-fly__trail"><i></i><i></i><i></i></div>' +
      `<div class="px-fly__body">${trolleyIcon}</div><span class="px-fly__shadow"></span></div>`;
    document.body.appendChild(layer);
    const cart = layer.querySelector('.px-fly__cart');
    const body = layer.querySelector('.px-fly__body');
    const cw = cart.offsetWidth;
    const ch = cart.offsetHeight;
    const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

    // Park on the lower edge of the product photo, clear of its name and price.
    const img = imageFor(form);
    const b = button.getBoundingClientRect();
    const src = img ? img.getBoundingClientRect() : b;
    const parkX = clamp(src.left + src.width / 2 - cw / 2, 12, vw - cw - 12);
    // Park on the bottom edge of the visible photo; the piece is sized to stay
    // above it, so it rises off the photo without ever covering the trolley.
    const seenTop = Math.max(src.top, 90);
    const seenBottom = Math.min(src.bottom, vh);
    const photoMid = seenBottom > seenTop ? (seenTop + seenBottom) / 2 : b.top;
    const parkY = clamp(img ? seenBottom - ch - 8 : b.top - ch - 10, 90, vh - ch - 24);
    const startX = rtl ? vw + 60 : -cw - 60;
    // Basket centre inside the trolley (the drawing faces the way it drives).
    const basketX = (rtl ? 80 - 48 : 48) * (cw / 80);
    const basketY = 13 * (ch / 64);

    let landedResolve;
    const landed = new Promise((resolve) => (landedResolve = resolve));
    gsap.set(cart, { x: startX, y: parkY });
    layer.classList.add('is-driving');
    const tl = gsap.timeline();
    tl.to(cart, { x: parkX, duration: 0.8, ease: 'power3.out' }, 0)
      .fromTo(body, { rotation: -8 * dir }, { rotation: 0, duration: 1, ease: 'elastic.out(1, 0.45)' }, 0.3)
      .add(() => layer.classList.remove('is-driving'), 0.7);

    let item = null;
    if (img) {
      const S = 120;
      item = document.createElement('img');
      item.className = 'px-fly__item';
      item.alt = '';
      item.src = img.currentSrc || img.src;
      layer.appendChild(item);
      // Lift off from the part of the photo that is actually on screen.
      const top = Math.max(src.top, 90);
      const bottom = Math.min(src.bottom, vh);
      const seen = Math.max(bottom - top, 60);
      // The trolley parks first, in full view. Only then does the piece lift
      // off the photo, sail over the trolley and drop into the basket from above.
      const room = Math.max(30, parkY - photoMid - 6);
      const from = Math.min(Math.min(src.width, seen) / S, 1.6, (2 * room) / S);
      const mid = 0.5;
      const to = 38 / S;
      const cy = bottom > top ? (top + bottom) / 2 : b.top;
      const startX = clamp(src.left + src.width / 2, S / 2, vw - S / 2) - S / 2;
      const landX = parkX + basketX - S / 2;
      const landY = parkY + basketY - S / 2;
      const peakY = Math.max(70, Math.min(cy - S / 2 - 110, parkY - 130));
      gsap.set(item, { x: startX, y: cy - S / 2, scale: from, borderRadius: 4 / from, autoAlpha: 0 });
      tl.to(item, { autoAlpha: 1, duration: 0.15 }, 0.7)
        .to(item, { scale: from * 0.75, y: `-=${70}`, duration: 0.35, ease: 'power2.out' }, 0.7)
        .to(item, { x: (startX + landX) / 2, y: peakY, scale: mid, borderRadius: 10, duration: 0.45, ease: 'power2.out' }, 1)
        .to(item, { x: landX, y: landY - 14, scale: to, borderRadius: 14, duration: 0.4, ease: 'power2.in' }, 1.45)
        .add(() => {
          // Into the basket: it now rides behind the wire front of the trolley.
          body.appendChild(item);
          gsap.set(item, { x: basketX - S / 2, y: basketY - S / 2 - 14, scale: to });
          gsap.to(item, { y: basketY - S / 2, duration: 0.35, ease: 'bounce.out' });
          sparkBurst(cart, 10, 46);
        }, 1.85)
        .to(body, { scaleX: 1.07, scaleY: 0.86, duration: 0.12, ease: 'power2.out' }, 1.85)
        .to(body, { scaleX: 1, scaleY: 1, duration: 0.6, ease: 'elastic.out(1, 0.35)' }, 1.97);
    }
    tl.add(() => landedResolve(), img ? 2.15 : 0.95);

    let gone = false;
    const timer = setTimeout(() => leave(false), 12000);

    function leave(ok) {
      if (gone) return;
      gone = true;
      clearTimeout(timer);
      if (flight === controller) flight = null;
      const out = gsap.timeline({ onComplete: () => layer.remove() });
      if (!ok) {
        holdCount(false);
        // Something went wrong: a little shake and the trolley backs away.
        out.to(body, { x: 5, duration: 0.06, repeat: 5, yoyo: true, ease: 'none' })
          .add(() => layer.classList.add('is-driving'))
          .to(cart, { x: startX, duration: 0.8, ease: 'power2.in' })
          .to(cart, { autoAlpha: 0, duration: 0.2 }, '-=0.2');
        return;
      }
      const icon = document.querySelector('#cart-icon-bubble');
      const ir = icon && icon.getBoundingClientRect();
      const iconShown = ir && ir.width > 0 && ir.bottom > 0 && ir.top < vh;
      layer.classList.add('is-driving');
      out.to(body, { rotation: 7 * dir, duration: 0.3, ease: 'power2.out' }, 0);
      if (iconShown) {
        out.to(cart, { x: ir.left + ir.width / 2 - cw / 2, duration: 0.9, ease: 'power2.in' }, 0.05)
          .to(cart, { y: ir.top + ir.height / 2 - ch / 2, duration: 0.9, ease: 'power4.in' }, 0.05)
          .to(cart, { scale: 0.25, autoAlpha: 0, duration: 0.3, ease: 'power2.in' }, 0.68)
          .add(() => catchAtIcon(icon), 0.85);
      } else {
        out.to(cart, { x: rtl ? -cw - 80 : vw + 80, duration: 0.9, ease: 'power2.in' }, 0.05).add(() => catchAtIcon(icon), 0.8);
      }
    }

    const controller = {
      success: () => landed.then(() => leave(true)),
      fail: () => landed.then(() => leave(false)),
      drop: () => {
        clearTimeout(timer);
        gone = true;
        tl.kill();
        holdCount(false);
        gsap.to(layer, { autoAlpha: 0, duration: 0.3, onComplete: () => layer.remove() });
      },
    };
    return controller;
  }

  // The count waits in the header until the trolley delivers it.
  function holdCount(on) {
    const icon = document.querySelector('#cart-icon-bubble');
    if (icon) icon.classList.toggle('px-awaiting', on);
  }

  function catchAtIcon(icon) {
    holdCount(false);
    bumpBubble(0);
    if (!icon) return;
    const svg = icon.querySelector('svg');
    if (svg) gsap.fromTo(svg, { scale: 1.35, rotation: -10 }, { scale: 1, rotation: 0, duration: 0.9, ease: 'elastic.out(1, 0.4)', clearProps: 'transform' });
    sparkBurst(icon, 10, 44);
  }

  function celebrateButton(button) {
    if (!button || !button.isConnected) return;
    let label = button.querySelector('.px-added-label');
    if (!label) {
      label = document.createElement('span');
      label.className = 'px-added-label';
      label.setAttribute('aria-hidden', 'true');
      label.innerHTML = `${checkIcon}<span>${(config.strings && config.strings.added) || 'Added'}</span>`;
      button.appendChild(label);
    }
    button.classList.add('px-added');
    sparkBurst(button, 18, 90);
    setTimeout(() => button.classList.remove('px-added'), 2200);
  }

  function bumpBubble(delay = 350) {
    if (!hasGsap || reduced) return;
    setTimeout(() => {
      const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
      if (bubble) gsap.fromTo(bubble, { scale: 0.3 }, { scale: 1, duration: 1, ease: 'elastic.out(1, 0.4)', clearProps: 'transform' });
    }, delay);
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event) return;
      if (event.source === 'cart-items') return settleRows();
      if (event.source !== 'product-form') return;
      newVariant = event.productVariantId;
      party = config.confetti !== false;
      if (lastAdd && Date.now() - lastAdd.at < 15000) celebrateButton(lastAdd.button);
      if (flight) {
        keepShut = true;
        setTimeout(() => (keepShut = false), 1500);
        holdCount(true);
        flight.success();
      } else {
        bumpBubble();
      }
    });
    subscribe(PUB_SUB_EVENTS.cartError, () => {
      if (flight) flight.fail();
    });
  }

  // When the bag opens: header, progress and items glide in one after another,
  // the free-shipping bar fills, and anything new gets a small celebration.
  if (bag && motion) {
    let isOpen = bag.classList.contains('active');
    new MutationObserver(() => {
      const now = bag.classList.contains('active');
      if (now && !isOpen) revealBag();
      isOpen = now;
    }).observe(bag, { attributes: true, attributeFilter: ['class'] });
  }

  function revealBag() {
    const items = Array.from(bag.querySelectorAll('.cart-item'));
    gsap.fromTo(
      bag.querySelectorAll('.drawer__header, .px-cart-progress, .drawer__footer > *, .cart-drawer__empty-content > *'),
      { y: 24, autoAlpha: 0 },
      { y: 0, autoAlpha: 1, duration: 0.9, ease: 'expo.out', stagger: 0.06, delay: 0.2, clearProps: 'transform,opacity,visibility' }
    );
    gsap.fromTo(
      items,
      { x: rtl ? -60 : 60, autoAlpha: 0 },
      { x: 0, autoAlpha: 1, duration: 1, ease: 'expo.out', stagger: 0.08, delay: 0.3, clearProps: 'transform,opacity,visibility' }
    );
    const bar = bag.querySelector('.px-cart-progress__bar');
    if (bar) gsap.from(bar, { scaleX: 0, transformOrigin: rtl ? '100% 50%' : '0% 50%', duration: 1.4, ease: 'expo.out', delay: 0.5, clearProps: 'transform' });
    let row = null;
    if (newVariant) {
      row = items.find((item) => item.querySelector(`a[href*="variant=${newVariant}"]`)) || null;
      if (row) {
        row.classList.remove('px-cart-new');
        void row.offsetWidth;
        row.classList.add('px-cart-new');
      }
      newVariant = null;
    }
    if (party && items.length) {
      party = false;
      setTimeout(() => celebrate(row), 450);
    }
  }

  function celebrate(row) {
    const inner = bag.querySelector('.drawer__inner');
    if (!inner || !bag.classList.contains('active')) return;
    const box = inner.getBoundingClientRect();
    const at = row && row.getBoundingClientRect();
    const ox = at ? at.left - box.left + Math.min(at.width * 0.35, 140) : box.width / 2;
    const oy = at ? at.top - box.top + at.height * 0.4 : box.height * 0.3;
    confetti(inner, ox, oy);

    const toast = document.createElement('div');
    toast.className = 'px-cart-toast';
    toast.setAttribute('role', 'status');
    const text = document.createElement('em');
    text.textContent = (config.strings && config.strings.celebrate) || '';
    toast.append(Object.assign(document.createElement('span'), { textContent: '✦' }), text, Object.assign(document.createElement('span'), { textContent: '✦' }));
    inner.appendChild(toast);
    gsap
      .timeline({ onComplete: () => toast.remove() })
      .fromTo(toast, { xPercent: -50, y: -16, scale: 0.85, autoAlpha: 0 }, { y: 0, scale: 1, autoAlpha: 1, duration: 0.8, ease: 'back.out(2.2)' })
      .to(toast.querySelectorAll('span'), { rotation: 180, duration: 1.2, ease: 'power2.inOut', stagger: 0.1 }, 0.2)
      .to(toast, { y: -10, autoAlpha: 0, duration: 0.5, ease: 'power2.in' }, 2.6);
  }

  // A light canvas confetti: gold flakes and little four-point stars.
  function confetti(host, ox, oy) {
    const canvas = document.createElement('canvas');
    canvas.className = 'px-confetti';
    host.appendChild(canvas);
    const w = host.clientWidth;
    const h = host.clientHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    const colors = ['#b48a4e', '#dcc39a', '#ecdcbf', '#8f6b3a', '#fff6e4', '#b48a4e'];
    const bits = Array.from({ length: 80 }, () => {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.9;
      const speed = 6 + Math.random() * 9;
      return {
        x: ox + (Math.random() - 0.5) * 30,
        y: oy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        spin: (Math.random() - 0.5) * 0.35,
        turn: Math.random() * Math.PI,
        size: 4 + Math.random() * 5,
        star: Math.random() < 0.3,
        color: colors[(Math.random() * colors.length) | 0],
      };
    });
    const start = performance.now();
    let last = start;
    const life = 2.4;
    function star(r) {
      ctx.beginPath();
      for (let i = 0; i < 8; i++) {
        const rad = i % 2 ? r * 0.32 : r;
        const a = (i * Math.PI) / 4;
        ctx.lineTo(Math.cos(a) * rad, Math.sin(a) * rad);
      }
      ctx.closePath();
      ctx.fill();
    }
    function frame(now) {
      const k = Math.min((now - last) / 16.67, 3);
      last = now;
      const t = (now - start) / 1000;
      ctx.clearRect(0, 0, w, h);
      const fade = Math.max(0, 1 - Math.max(0, t - 1.5) / (life - 1.5));
      for (const p of bits) {
        p.vy += 0.3 * k;
        p.vx *= Math.pow(0.985, k);
        p.vy *= Math.pow(0.985, k);
        p.x += p.vx * k;
        p.y += p.vy * k;
        p.turn += p.spin * k;
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.turn);
        ctx.fillStyle = p.color;
        if (p.star) star(p.size * 0.9);
        else ctx.fillRect(-p.size / 2, -p.size / 4, p.size, (p.size / 2) * Math.abs(Math.cos(p.turn * 2)) + 1);
        ctx.restore();
      }
      if (t < life && bag.classList.contains('active')) requestAnimationFrame(frame);
      else canvas.remove();
    }
    requestAnimationFrame(frame);
  }

  // Removing a piece: it slides out, then the rest of the bag closes the gap
  // smoothly instead of jumping (FLIP across Dawn's re-render).
  let rowsBefore = null;
  const rowKey = (row) => {
    const link = row.querySelector('a[href*="variant="], a.cart-item__name');
    return link ? link.getAttribute('href') : null;
  };
  if (bag && hasGsap && !reduced) {
    bag.addEventListener(
      'click',
      (event) => {
        const remove = event.target.closest && event.target.closest('cart-remove-button');
        if (!remove) return;
        const row = remove.closest('.cart-item');
        if (!row || row.dataset.pxLeaving) return;
        event.preventDefault();
        event.stopPropagation();
        row.dataset.pxLeaving = '1';
        // The pieces below move up straight away, so there is no hole while
        // the store answers; FLIP then matches Dawn's fresh rows to them.
        const rows = Array.from(bag.querySelectorAll('.cart-item'));
        const below = rows.slice(rows.indexOf(row) + 1);
        const gap = row.getBoundingClientRect().height;
        rowsBefore = new Map();
        rows.forEach((other) => {
          if (other !== row) rowsBefore.set(rowKey(other), other.getBoundingClientRect().top - (below.includes(other) ? gap : 0));
        });
        gsap
          .timeline()
          .to(row, { x: rtl ? -50 : 50, autoAlpha: 0, duration: 0.4, ease: 'power2.in' })
          .to(below, { y: -gap, duration: 0.6, ease: 'expo.out' }, 0.3)
          .add(() => remove.dispatchEvent(new MouseEvent('click', { bubbles: true })), 0.4);
      },
      true
    );
  }

  function settleRows() {
    if (!rowsBefore || !bag) return;
    const before = rowsBefore;
    rowsBefore = null;
    bag.querySelectorAll('.cart-item').forEach((row) => {
      const top = before.get(rowKey(row));
      if (top == null) return;
      const delta = top - row.getBoundingClientRect().top;
      if (Math.abs(delta) > 1) gsap.fromTo(row, { y: delta }, { y: 0, duration: 0.7, ease: 'expo.out', clearProps: 'transform' });
    });
  }

  /* ---------- Search: the lens opens from the icon ---------- */
  const lensRadius = (sheet, r) => {
    const x = r.left + r.width / 2;
    const y = r.top + r.height / 2;
    sheet.style.setProperty('--lr', `${Math.ceil(Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y)))}px`);
  };
  document.querySelectorAll('details-modal.px-search').forEach((modal) => {
    const details = modal.querySelector('details');
    const summary = modal.querySelector('summary');
    const sheet = modal.querySelector('.px-search__modal');
    const input = modal.querySelector('input[type="search"]');
    if (!details || !summary || !sheet) return;

    // The lens grows from wherever the icon sits on screen.
    summary.addEventListener('click', () => {
      const r = summary.getBoundingClientRect();
      sheet.style.setProperty('--sx', `${Math.round(r.left + r.width / 2)}px`);
      sheet.style.setProperty('--sy', `${Math.round(r.top + r.height / 2)}px`);
      lensRadius(sheet, r);
    }, true);

    // Let the lens shrink back into the icon before Dawn closes the modal.
    customElements.whenDefined('details-modal').then(() => {
      if (modal.pxClosePatched) return;
      modal.pxClosePatched = true;
      const close = modal.close.bind(modal);
      modal.close = (focusToggle) => {
        if (!details.open || modal.classList.contains('px-closing') || reduced) return close(focusToggle);
        modal.classList.add('px-closing');
        setTimeout(() => {
          modal.classList.remove('px-closing');
          close(focusToggle);
        }, 580);
      };
      // Dawn bound its close button to the old method, so route it here.
      const closeButton = modal.querySelector('button[type="button"]');
      if (closeButton) closeButton.addEventListener('click', (event) => {
        event.stopImmediatePropagation();
        modal.close();
      }, true);
    });

    // Typing hints in the empty field.
    const hints = (input && input.dataset.pxTyping ? input.dataset.pxTyping.split('|') : []).map((s) => s.trim()).filter(Boolean);
    const original = input ? input.placeholder : '';
    let typing = null;
    const stopTyping = () => {
      clearTimeout(typing);
      typing = null;
      if (input) input.placeholder = original;
    };
    const startTyping = () => {
      if (!input || !hints.length || reduced) return;
      let word = 0;
      let length = 0;
      let deleting = false;
      const step = () => {
        if (!details.open) return stopTyping();
        const text = hints[word % hints.length];
        length += deleting ? -1 : 1;
        input.placeholder = text.slice(0, Math.max(length, 0));
        let delay = deleting ? 35 : 75;
        if (!deleting && length >= text.length) { deleting = true; delay = 1400; }
        else if (deleting && length <= 0) { deleting = false; word++; delay = 350; }
        typing = setTimeout(step, delay);
      };
      typing = setTimeout(step, 700);
    };

    // A popular search fills the field and asks for suggestions right away.
    modal.querySelectorAll('[data-px-term]').forEach((chip) => {
      chip.addEventListener('click', (event) => {
        if (!input || !modal.querySelector('predictive-search')) return;
        event.preventDefault();
        stopTyping();
        input.value = chip.dataset.pxTerm;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
      });
    });

    // Every part of the sheet arrives in its own way.
    const entrance = () => {
      if (!hasGsap || reduced) return;
      const kicker = modal.querySelector('.px-search__kicker');
      const tl = gsap.timeline({ delay: 0.25 });
      if (kicker && window.SplitText && !kicker.pxChars) {
        kicker.pxChars = SplitText.create(kicker, { type: textUnit('chars') })[textUnit('chars')];
      }
      if (kicker && kicker.pxChars) tl.fromTo(kicker.pxChars, { autoAlpha: 0, y: 14, rotation: 8 }, { autoAlpha: 1, y: 0, rotation: 0, duration: 0.5, ease: 'power3.out', stagger: 0.018 }, 0);
      tl.fromTo(modal.querySelector('.px-search__field'), { scaleX: 0.12, autoAlpha: 0, transformOrigin: rtl ? '0% 50%' : '100% 50%' }, { scaleX: 1, autoAlpha: 1, duration: 0.9, ease: 'expo.out', clearProps: 'transform' }, 0.1)
        .fromTo(modal.querySelector('.px-search__lens'), { rotation: -120, scale: 0.4 }, { rotation: 0, scale: 1, duration: 0.9, ease: 'back.out(2)' }, 0.35)
        .fromTo(modal.querySelectorAll('.px-search__title'), { autoAlpha: 0, letterSpacing: '0.6em' }, { autoAlpha: 1, letterSpacing: '0.22em', duration: 0.8, ease: 'power2.out', stagger: 0.12 }, 0.45)
        .fromTo(modal.querySelectorAll('.px-search__chip'), { autoAlpha: 0, y: 30, rotation: () => gsap.utils.random(-16, 16) }, { autoAlpha: 1, y: 0, rotation: 0, duration: 0.7, ease: 'back.out(2.2)', stagger: 0.045, clearProps: 'transform' }, 0.55)
        .fromTo(modal.querySelectorAll('.px-search__cat-img'), { xPercent: 160, rotation: 220, autoAlpha: 0 }, { xPercent: 0, rotation: 0, autoAlpha: 1, duration: 1, ease: 'power3.out', stagger: 0.07, clearProps: 'transform' }, 0.65)
        .fromTo(modal.querySelectorAll('.px-search__cat-name'), { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5, stagger: 0.07 }, 1.1)
        .fromTo(modal.querySelectorAll('.px-search__item'), { autoAlpha: 0, filter: 'blur(14px)', scale: 1.05 }, { autoAlpha: 1, filter: 'blur(0px)', scale: 1, duration: 0.9, ease: 'power2.out', stagger: 0.08, clearProps: 'filter,transform' }, 0.8);
    };

    const headerSection = modal.closest('.section-header');
    new MutationObserver(() => {
      if (headerSection) headerSection.classList.toggle('px-search-open', details.open);
      if (details.open) {
        entrance();
        startTyping();
      } else stopTyping();
    }).observe(details, { attributes: true, attributeFilter: ['open'] });
    if (input) input.addEventListener('input', () => {
      if (input.value) stopTyping();
      modal.classList.toggle('has-query', !!input.value.trim());
    });
    modal.querySelector('form') && modal.querySelector('form').addEventListener('reset', () => modal.classList.remove('has-query'));
    // Fetch the sheet's photos as soon as someone reaches for the icon.
    const warm = () => modal.querySelectorAll('.px-search__extras img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
    summary.addEventListener('pointerenter', warm, { once: true });
    summary.addEventListener('focus', warm, { once: true });
    summary.addEventListener('touchstart', warm, { once: true, passive: true });
  });

  // Links to the search page (footer, menus) open the same lens as the icon.
  // A search page visited without a query opens it too.
  const openLens = (from) => {
    const modal = Array.from(document.querySelectorAll('details-modal.px-search')).find((m) => {
      const s = m.querySelector('summary');
      return s && s.getClientRects().length;
    });
    const summary = modal && modal.querySelector('summary');
    const details = modal && modal.querySelector('details');
    if (!summary || !details) return false;
    if (!details.open) summary.click();
    const field = modal.querySelector('input[type="search"]');
    if (field) requestAnimationFrame(() => field.focus({ preventScroll: true }));
    const sheet = modal.querySelector('.px-search__modal');
    if (from && sheet) {
      const r = from.getBoundingClientRect();
      sheet.style.setProperty('--sx', `${Math.round(r.left + r.width / 2)}px`);
      sheet.style.setProperty('--sy', `${Math.round(r.top + r.height / 2)}px`);
      lensRadius(sheet, r);
    }
    return true;
  };
  // Escape closes the lens even when focus has wandered outside it.
  document.addEventListener('keyup', (event) => {
    if (event.key !== 'Escape') return;
    const modal = document.querySelector('details-modal.px-search details[open]');
    if (modal && !modal.contains(event.target)) modal.closest('details-modal').close();
  });
  const isBareSearch = (url) => /\/search\/?$/.test(url.pathname) && !url.searchParams.get('q');
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest && event.target.closest('a[href]');
    if (!link || link.target === '_blank' || link.closest('.px-search')) return;
    let url;
    try { url = new URL(link.href, location.href); } catch (e) { return; }
    if (url.origin !== location.origin || !isBareSearch(url)) return;
    if (!document.querySelector('details-modal.px-search summary')) return;
    event.preventDefault();
    // Open after this click has finished, or Dawn reads it as a click outside.
    setTimeout(() => openLens(link) || (location.href = url.href), 0);
  }, true);
  if (isBareSearch(new URL(location.href)) && document.querySelector('.px-spage')) {
    customElements.whenDefined('details-modal').then(() => introDone.then(() => setTimeout(() => openLens(document.querySelector('.px-shead__lens')), 400)));
  }

  // Desktop header: dropdowns open on hover. A short delay on leaving lets the
  // pointer cross the gap into the panel; click and keyboard keep working.
  const hoverMenus = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
  document.querySelectorAll('header-menu details').forEach((details) => {
    const summary = details.querySelector('summary');
    if (!summary) return;
    let timer;
    const setOpen = (open) => {
      details.open = open;
      summary.setAttribute('aria-expanded', open ? 'true' : 'false');
    };
    details.addEventListener('mouseenter', () => {
      clearTimeout(timer);
      if (!hoverMenus.matches || details.open) return;
      document.querySelectorAll('header-menu details[open]').forEach((other) => other !== details && (other.open = false));
      details.dataset.pxHover = '1';
      setOpen(true);
    });
    details.addEventListener('mouseleave', () => {
      clearTimeout(timer);
      if (!details.dataset.pxHover) return;
      timer = setTimeout(() => {
        delete details.dataset.pxHover;
        setOpen(false);
      }, 180);
    });
    // A mouse click on a hover-opened menu should not snap it shut.
    summary.addEventListener('click', (event) => {
      if (details.dataset.pxHover && event.detail > 0) event.preventDefault();
    });
  });

  // Cart page: Dawn refreshes its own sections after a change; refresh the
  // free-shipping intro too so its bar matches the new total.
  const cartIntro = document.querySelector('.px-cart-intro');
  if (cartIntro && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    const wrapper = cartIntro.closest('.shopify-section');
    const sectionId = wrapper && wrapper.id.replace('shopify-section-', '');
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (!sectionId) return;
      fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`)
        .then((r) => r.text())
        .then((html) => {
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
          if (fresh) wrapper.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    });
  }

  // FAQ: the search box filters questions; answers open and close smoothly.
  document.querySelectorAll('.px-faq').forEach((faq) => {
    const filter = faq.querySelector('[data-px-faq-filter]');
    const items = Array.from(faq.querySelectorAll('.px-q'));
    const empty = faq.querySelector('.px-faq__empty');
    if (filter) {
      filter.addEventListener('input', () => {
        const term = filter.value.trim().toLowerCase();
        let shown = 0;
        items.forEach((q) => {
          const match = !term || q.textContent.toLowerCase().includes(term);
          q.classList.toggle('is-hidden', !match);
          if (match) shown++;
        });
        if (empty) empty.hidden = shown > 0;
      });
    }
    if (!hasGsap || reduced) return;
    items.forEach((q) => {
      const summary = q.querySelector('summary');
      const answer = q.querySelector('.px-q__answer');
      if (!summary || !answer) return;
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        gsap.killTweensOf(answer);
        // The padding folds with the height, so nothing jumps when [open] goes.
        // The gold line and the toggle turn back as the answer starts closing.
        if (q.open && !q.classList.contains('is-closing')) {
          q.classList.add('is-closing');
          gsap.to(answer, {
            height: 0, paddingBottom: 0, autoAlpha: 0, duration: 0.5, ease: 'power3.inOut',
            onComplete: () => {
              q.open = false;
              q.classList.remove('is-closing');
              gsap.set(answer, { clearProps: 'height,paddingBottom,opacity,visibility' });
            },
          });
        } else {
          const from = q.open
            ? { height: answer.offsetHeight, paddingBottom: getComputedStyle(answer).paddingBottom, autoAlpha: gsap.getProperty(answer, 'opacity') }
            : { height: 0, paddingBottom: 0, autoAlpha: 0 };
          q.classList.remove('is-closing');
          q.open = true;
          // Measure the full answer first, then grow to exactly that.
          gsap.set(answer, { clearProps: 'height,paddingBottom' });
          const pad = getComputedStyle(answer).paddingBottom;
          const full = answer.offsetHeight;
          gsap.fromTo(answer, from, { height: full, paddingBottom: pad, autoAlpha: 1, duration: 0.6, ease: 'power3.out', clearProps: 'height,paddingBottom' });
        }
      });
    });
  });

  // Collection pills scroll sideways on phones: bring the current category into view.
  document.querySelectorAll('.px-colhero__pills').forEach((list) => {
    const current = list.querySelector('.is-current');
    if (!current || list.scrollWidth <= list.clientWidth) return;
    const parent = current.parentElement;
    const target = parent.offsetLeft - (list.clientWidth - parent.offsetWidth) / 2;
    list.scrollLeft = rtl ? -Math.max(0, list.scrollWidth - list.clientWidth - target) : Math.max(0, target);
  });

  // Collection mega menu: pointing at (or tabbing to) a collection shows its
  // products and photo.
  class PxMega extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-px-mega-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-px-mega-panel]'));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('pointerenter', () => this.show(i));
        tab.addEventListener('focus', () => this.show(i));
      });
      // The panel's photos start loading as soon as the pointer reaches the
      // menu bar (or the menu gets focus), so they are there when it opens.
      const bar = this.closest('.header__inline-menu');
      const warm = () => this.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
      if (bar) {
        bar.addEventListener('pointerenter', warm, { once: true });
        bar.addEventListener('focusin', warm, { once: true });
      }
    }

    show(index) {
      this.tabs.forEach((tab, i) => tab.classList.toggle('is-active', i === index));
      this.panels.forEach((panel, i) => panel.classList.toggle('is-active', i === index));
    }
  }

  // "The art of wearing" showcase. A full-screen stage stays in place while
  // the section scrolls past; each step of the scroll brings the next photo
  // up from the bottom edge (the photo itself holds still, only its edge
  // moves) and moves the underline to its name. Scroll-linked on purpose:
  // the user asked for this section to follow the scroll.
  class PxWear extends HTMLElement {
    connectedCallback() {
      this.layers = Array.from(this.querySelectorAll('.px-wear__layer'));
      this.links = Array.from(this.querySelectorAll('.px-wear__link'));
      this.notes = Array.from(this.querySelectorAll('.px-wear__note'));
      this.titles = Array.from(this.querySelectorAll('.px-wear__title'));
      this.stage = this.querySelector('.px-wear__stage');
      this.steps = this.layers.length - 1;
      this.current = -1;
      this.setActive(0);
      this.warm();
      if (this.steps < 1) return;
      if (hasST && !reduced) this.scrub();
      else this.stepper();
    }

    disconnectedCallback() {
      if (this.trigger) this.trigger.kill();
      if (this.tl) this.tl.kill();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.io) this.io.disconnect();
    }

    // Every photo is fetched a screen or so before the section arrives, so
    // none of them is still loading as its edge sweeps up.
    warm() {
      const imgs = this.querySelectorAll('.px-wear__img img');
      if (!('IntersectionObserver' in window)) return;
      this.io = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        imgs.forEach((img) => (img.loading = 'eager'));
        this.io.disconnect();
      }, { rootMargin: '150% 0px' });
      this.io.observe(this);
    }

    setActive(index) {
      if (index === this.current) return;
      this.current = index;
      this.links.forEach((link, i) => link.classList.toggle('is-active', i === index));
      this.notes.forEach((note, i) => note.classList.toggle('is-active', i === index));
      this.titles.forEach((title, i) => title.classList.toggle('is-active', i === index));
    }

    scrub() {
      const tl = gsap.timeline({ defaults: { ease: 'none', duration: 1 }, paused: true });
      this.layers.slice(1).forEach((layer, k) => {
        tl.fromTo(layer, { y: 0, yPercent: 100 }, { yPercent: 0 }, k).fromTo(layer.firstElementChild, { y: 0, yPercent: -100 }, { yPercent: 0 }, k);
      });
      // The underline follows the photos as they are drawn (after the scrub's
      // smoothing), switching once the next photo covers half the screen.
      tl.eventCallback('onUpdate', () => this.setActive(Math.round(tl.progress() * this.steps)));
      this.tl = tl;
      const headerH = () => parseFloat(getComputedStyle(html).getPropertyValue('--px-header-h')) || 0;
      this.trigger = ScrollTrigger.create({
        trigger: this,
        start: () => `top ${headerH()}`,
        end: 'bottom bottom',
        animation: tl,
        scrub: 0.6,
        invalidateOnRefresh: true,
      });
    }

    // Reduced motion: the photos switch at each step without moving.
    stepper() {
      this.classList.add('px-wear--static');
      this.onScroll = () => {
        const range = this.offsetHeight - this.stage.offsetHeight;
        const stuckAt = parseFloat(getComputedStyle(this.stage).top) || 0;
        const progress = range > 0 ? Math.min(1, Math.max(0, (stuckAt - this.getBoundingClientRect().top) / range)) : 0;
        const index = Math.round(progress * this.steps);
        this.setActive(index);
        this.layers.forEach((layer, i) => layer.classList.toggle('is-shown', i <= index));
      };
      this.onScroll();
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
  }

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };
  define('px-mega', PxMega);
  define('px-wear', PxWear);
  define('px-hero', PxHero);
  define('px-carousel', PxCarousel);
  define('px-tabs', PxTabs);
  define('px-countdown', PxCountdown);
  define('px-hotspots', PxHotspots);
  define('px-sticky-cart', PxStickyCart);

  // Everything above has run: the gold thread may start writing the name.
  if (hasLoader) {
    requestAnimationFrame(() => {
      const at = window.pxWriteAt || performance.now();
      window.pxWriteAt = at;
      html.classList.add('px-write');
      markWriting(at);
    });
  }
})();
